import React, { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { httpsCallable } from "firebase/functions";
import { functions, auth } from "../../firebase/config";
import DashboardWrapper from "../../components/layout/DashboardWrapper";
import { adminService } from "../../services/adminService";
import { UserData } from "../../types/user";
import { invitationService } from "../../services/invitationService";
import { Invitation } from "../../types/invitation";
import {
    ATHLETE_SUBSCRIPTION_LABELS,
    ATHLETE_SUBSCRIPTION_OPTIONS,
    AthleteSubscription,
    DEFAULT_ORGANISATION_CONFIG,
    Organisation,
    OrganisationConfig,
    OrganisationMember,
    OrganisationMemberNote,
    OrganisationSubcollectionEntry,
    OwnerDocIssue,
    resolveAthleteSubscription,
} from "../../types/organisation";
import {
    createOrganisation,
    fetchOrganisationById,
    fetchOrganisationMemberNotes,
    fetchOrganisationSubcollection,
    getOrganisationConfig,
    saveOrganisationConfig,
    updateCoachTeams,
    updateMedicalTeams,
    updateMemberGroups,
    updateMemberTeam,
    updateOrganisationGroups,
    updateOrganisationName,
    updateOrganisationTeams,
} from "../../services/orgService";
import {
    ArrowLeftIcon,
    BuildingOffice2Icon,
    UserGroupIcon,
    PencilSquareIcon,
    PlusIcon,
    XMarkIcon,
    ArrowPathIcon,
    PaperAirplaneIcon,
    CheckCircleIcon,
    Cog6ToothIcon,
    BanknotesIcon,
    ArrowDownTrayIcon,
    ExclamationTriangleIcon,
} from "@heroicons/react/24/outline";

type EnrichedUser = UserData & { uid: string };
type RecheckStatus = "checking" | "success" | "failed";

interface RecheckEntry {
    id: string;
    role: "owner" | "coach" | "medical" | "athlete";
    userId: string;
    status: RecheckStatus;
    error?: string;
}

const formatMemberDate = (value: unknown): string => {
    const date = value && typeof (value as { toDate?: unknown }).toDate === "function"
        ? (value as { toDate: () => Date }).toDate()
        : value instanceof Date ? value : null;
    if (!date) return "—";
    return date.toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" });
};

const formatSubscriptionLabel = (subscriptionType: string, subscriptionVia: string): string => {
    const type = subscriptionType.trim() || "default";
    const via = subscriptionVia.trim() || "default";
    return type === "default" ? "default" : `${type} via ${via}`;
};

const toDateInputValue = (date?: Date): string => (date ? date.toISOString().split("T")[0] : "");

const calculateBillingPeriodEnd = (start: Date, billingPeriod?: OrganisationConfig["billingPeriod"]): Date => {
    const end = new Date(start);
    end.setMonth(end.getMonth() + (billingPeriod === "quarterly" ? 3 : 12));
    return end;
};

// Subset of the raw Stripe Invoice object returned by getPeakEliteCoachInvoices.
interface StripeInvoiceSummary {
    id: string;
    number: string | null;
    status: string | null;
    created: number;
    total: number;
    amount_paid: number;
    amount_due: number;
    currency: string;
    invoice_pdf: string | null;
    hosted_invoice_url: string | null;
}

const formatInvoiceDate = (unixSeconds: number): string =>
    new Date(unixSeconds * 1000).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" });

const formatInvoiceAmount = (amountMinor: number, currency: string): string => {
    const normalizedCurrency = /^[a-zA-Z]{3}$/.test(currency) ? currency.toUpperCase() : "USD";
    try {
        return new Intl.NumberFormat("en-AU", { style: "currency", currency: normalizedCurrency }).format(amountMinor / 100);
    } catch {
        return `${(amountMinor / 100).toFixed(2)} ${normalizedCurrency}`;
    }
};

const AffectsCallout: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <p className="text-blue-700 bg-blue-50 rounded-lg p-3 border border-blue-200">
        <span className="font-semibold">Affects:</span> {children}
    </p>
);

const AthleteSubscriptionSelect: React.FC<{
    orgType: OrganisationConfig["orgType"];
    value: AthleteSubscription;
    onChange: (value: AthleteSubscription) => void;
    selectClassName?: string;
    labelClassName?: string;
    helpClassName?: string;
}> = ({
    orgType, value, onChange,
    selectClassName = "select select-bordered select-sm w-40 disabled:opacity-50",
    labelClassName = "text-xs font-medium text-gray-500 uppercase tracking-wider",
    helpClassName = "text-xs text-gray-400",
}) => {
    const commercial = orgType === "commercial";
    return (
        <div className="flex flex-col gap-1.5">
            <label className={labelClassName}>Athlete subscription</label>
            <p className={helpClassName}>
                {commercial
                    ? "Plan athletes receive by being in this organisation. Premium is the default. Basic means no subscription from the organisation. Saving updates every athlete."
                    : "Only commercial organisations can choose this. Athletes in individual and team organisations get Premium."}
            </p>
            <select
                value={commercial ? value : "premium"}
                disabled={!commercial}
                onChange={(e) => onChange(e.target.value as AthleteSubscription)}
                className={selectClassName}
            >
                {ATHLETE_SUBSCRIPTION_OPTIONS.map((option) => (
                    <option key={option} value={option}>{ATHLETE_SUBSCRIPTION_LABELS[option]}</option>
                ))}
            </select>
        </div>
    );
};

const invoiceStatusBadgeClass = (status: string | null): string => {
    switch (status) {
        case "paid": return "badge-success text-white";
        case "open": return "badge-warning text-white";
        case "uncollectible":
        case "void": return "badge-error text-white";
        default: return "badge-ghost";
    }
};

// ── Create form ───────────────────────────────────────────────────────────────

const ORG_TYPE_INFO: Record<OrganisationConfig["orgType"], {
    label: string;
    who: string;
    limits: string;
    selfServe: string;
}> = {
    individual: {
        label: "Individual",
        who: "A single coach working with their own athletes.",
        limits: "1 coach, 1 team, 1–20 athletes.",
        selfServe: "Client manages their own billing (seats, invoices, cancelling) in PeakAMS.",
    },
    team: {
        label: "Team",
        who: "A club or organisation with several coaches.",
        limits: "Up to 10 coaches, up to 10 teams, 20–200 athletes.",
        selfServe: "Client manages their own billing (seats, invoices, cancelling) in PeakAMS.",
    },
    commercial: {
        label: "Commercial",
        who: "Bespoke or enterprise deals (200+ athletes, negotiated terms).",
        limits: "Every limit is set by hand, there are no preset caps.",
        selfServe: "No self-serve billing. Every plan change or cancellation is done by the Peak team.",
    },
};

const CreateOrganisationForm: React.FC = () => {
    const navigate = useNavigate();
    const [name, setName] = useState("");
    const [config, setConfig] = useState<OrganisationConfig>({
        ...DEFAULT_ORGANISATION_CONFIG,
        orgType: "commercial",
        maxAthletes: 5,
        maxCoaches: 1,
        maxTeams: 1,
    });
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const setConfigField = <K extends keyof OrganisationConfig>(key: K, value: OrganisationConfig[K]) =>
        setConfig((prev) => ({ ...prev, [key]: value }));

    const isCommercial = config.orgType === "commercial";
    const typeInfo = ORG_TYPE_INFO[config.orgType];
    const athleteBounds: Record<"individual" | "team", [number, number]> = { individual: [1, 20], team: [20, 200] };
    const bounds = config.orgType === "commercial" ? null : athleteBounds[config.orgType];
    const athletesOutOfRange = bounds !== null && (config.maxAthletes < bounds[0] || config.maxAthletes > bounds[1]);
    const pasExceedsAthletes = config.maxAthletesForPas > config.maxAthletes;

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!name.trim()) return;
        setSaving(true);
        setError(null);
        try {
            const orgId = await createOrganisation(name.trim());
            const configToSave: OrganisationConfig = config.orgType === "commercial"
                ? { ...config, athleteSubscription: resolveAthleteSubscription(config.athleteSubscription) }
                : { ...config, athleteSubscription: "premium" };
            await saveOrganisationConfig(orgId, configToSave);
            navigate(`/commercial/organisations/${orgId}`, { replace: true });
        } catch (err) {
            console.error("[OrganisationDetailPage] Failed to create:", err);
            setError("Failed to create organisation. Please try again.");
        } finally {
            setSaving(false);
        }
    };

    const limitFields: { key: "maxAthletes" | "maxAthletesForPas" | "maxCoaches" | "maxTeams"; label: string; help: string; affects: string }[] = [
        {
            key: "maxAthletes",
            label: "Max Athletes",
            help: "How many athletes can be active in the organisation. These are the athlete monitoring seats.",
            affects: "The price of the plan and how many athletes the organisation can add. The client cannot lower this below their current active athletes.",
        },
        {
            key: "maxAthletesForPas",
            label: "Add on: Max Athletes for Programs and Sessions",
            help: "How many athletes can have a PAS (Programs and Sessions) seat, which lets coaches build programs and sessions for them. Use 0 if the client isn't buying it.",
            affects: "The Programming line on the invoice. It should never be higher than Max Athletes.",
        },
        {
            key: "maxCoaches",
            label: "Max Coaches",
            help: isCommercial
                ? "How many coaches the organisation can have. Set this to match the agreed deal."
                : "Preset by the organisation type, so it is locked. Switch to Commercial to set it by hand.",
            affects: "How many coaches can be added to the organisation.",
        },
        {
            key: "maxTeams",
            label: "Max Teams",
            help: isCommercial
                ? "How many teams the organisation can create. Set this to match the agreed deal."
                : "Preset by the organisation type, so it is locked. Switch to Commercial to set it by hand.",
            affects: "How many teams can be created to group athletes and coaches.",
        },
    ];

    return (
        <div className="flex flex-col gap-20 w-full max-w-3xl">
            <div className="flex flex-col gap-1">
                <h1 className="text-xl font-bold text-gray-900">New Organisation</h1>
                <p className="text-gray-500 mt-0.5">
                    Work through the four steps below. Each field explains what it does and what it changes.
                    Everything here can be edited later from the organisation&apos;s Settings tab.
                </p>
            </div>

            <form onSubmit={handleSubmit} className="flex flex-col w-full gap-5">
                {/* 1. Name */}
                <div className="flex flex-col gap-4 bg-white p-6 rounded-xl border border-gray-200">
                    <div>
                        <h2 className="font-semibold text-gray-900">1. Organisation name</h2>
                        <p className="text-gray-500">The name the coach will see for this organisation, for example the club or coach&apos;s business name.</p>
                    </div>
                    <input
                        type="text"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="e.g. Acme Sports Club"
                        className="input input-bordered w-full text-base"
                        required
                    />
                </div>

                {/* 2. Type */}
                <div className="flex flex-col gap-4 bg-white p-6 rounded-xl border border-gray-200">
                    <div>
                        <h2 className="font-semibold text-gray-900">2. Organisation type</h2>
                        <p className="text-gray-500 mt-0.5">Choose this first, because it sets sensible defaults for the fields below. It decides the coach and team limits, which athlete range is allowed, and whether the client can manage their own billing.</p>
                    </div>
                    <p className="text-blue-700 bg-blue-50 rounded-lg p-3 border border-blue-200">
                        <span className="font-semibold">Affects:</span> Changing it presets Max Coaches and Max Teams (Individual: 1 and 1, Team: 10 and 10) and locks them. Only Commercial lets you edit them, and only Commercial can choose the athlete subscription in step 4.
                    </p>
                    <select
                        value={config.orgType}
                        onChange={(e) => {
                            const nextType = e.target.value as OrganisationConfig["orgType"];
                            setConfig((prev) => ({
                                ...prev,
                                orgType: nextType,
                                ...(nextType === "individual" && { maxCoaches: 1, maxTeams: 1 }),
                                ...(nextType === "team" && { maxCoaches: 10, maxTeams: 10 }),
                                ...(nextType !== "commercial" && { athleteSubscription: "premium" as const }),
                            }));
                        }}
                        className="select select-bordered w-full text-base"
                    >
                        <option value="individual">Individual</option>
                        <option value="team">Team</option>
                        <option value="commercial">Commercial</option>
                    </select>
                    {!isCommercial && (
                        <p className="text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
                            Individual and Team organisations should be set up by the user themselves at ams.peak.app, which
                            starts their free trial and billing. Only create one here if you have a specific reason to.
                        </p>
                    )}
                    <div className="rounded-lg bg-gray-50 border border-gray-100 p-3 text-gray-600 flex flex-col gap-1">
                        <p><span className="font-semibold">{typeInfo.label}:</span> {typeInfo.who}</p>
                        <p><span className="font-semibold">Limits:</span> {typeInfo.limits}</p>
                        <p><span className="font-semibold">Billing:</span> {typeInfo.selfServe}</p>
                    </div>
                </div>

                {/* 3. Seats */}
                <div className="flex flex-col gap-4 bg-white p-6 rounded-xl border border-gray-200">
                    <div>
                        <h2 className="font-semibold text-gray-900">3. Seats and limits</h2>
                        <p className="text-gray-500 mt-0.5">How much of the product the organisation is allowed to use. These limits drive pricing.</p>
                    </div>
                    <div className="flex flex-col gap-4">
                        {limitFields.map(({ key, label, help, affects }) => {
                            const isCapacityField = key === "maxCoaches" || key === "maxTeams";
                            const disabled = isCapacityField && !isCommercial;
                            return (
                                <div key={key} className="flex flex-col gap-1.5 border-b pb-6">
                                    <label className="font-medium text-gray-800">{label}</label>
                                    <p className="text-gray-500">{help}</p>
                                    <p className="text-blue-700 bg-blue-50 rounded-lg p-3 border border-blue-200">
                                        <span className="font-semibold">Affects:</span> {affects}
                                    </p>
                                    <input
                                        type="number"
                                        min={0}
                                        value={config[key]}
                                        disabled={disabled}
                                        onChange={(e) => setConfigField(key, parseInt(e.target.value, 10) || 0)}
                                        className="input input-bordered w-full text-base disabled:opacity-50"
                                    />
                                </div>
                            );
                        })}
                    </div>
                    {athletesOutOfRange && bounds && (
                        <p className="text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
                            {typeInfo.label} organisations are normally {bounds[0]}–{bounds[1]} athletes. This is outside that range.
                            Use the Commercial type if this is a bespoke deal.
                        </p>
                    )}
                    {pasExceedsAthletes && (
                        <p className="text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
                            Max Athletes (PAS) is higher than Max Athletes. PAS seats can&apos;t exceed the athlete limit.
                        </p>
                    )}
                </div>

                {/* 4. Billing & subscription */}
                <div className="flex flex-col gap-6 bg-white p-6 rounded-xl border border-gray-200">
                    <div>
                        <h2 className="font-semibold text-gray-900">4. Billing and subscriptions</h2>
                        <p className="text-gray-500 mt-0.5">How the organisation is invoiced and what its athletes receive.</p>
                    </div>

                    <div className="flex flex-col gap-3 pb-6 border-b">
                        <label className="font-medium text-gray-800">Billing Period</label>
                        <p className="text-gray-500">
                            How often the organisation is invoiced. Annual is the discounted option (about 30% cheaper overall). Quarterly costs more in exchange for flexibility.
                        </p>
                        <p className="text-blue-700 bg-blue-50 rounded-lg p-3 border border-blue-200">
                            <span className="font-semibold">Affects:</span> The amount on each invoice and how long each billing period runs. You can set the billing period start date later in Settings.
                        </p>
                        <select
                            value={config.billingPeriod}
                            onChange={(e) => setConfigField("billingPeriod", e.target.value as OrganisationConfig["billingPeriod"])}
                            className="select select-bordered w-full text-base"
                        >
                            <option value="quarterly">Quarterly</option>
                            <option value="annually">Annually</option>
                        </select>
                    </div>

                    <div className="flex flex-col gap-3 pb-6 border-b">
                        <label className="font-medium text-gray-800">Onboarding Requested</label>
                        <p className="text-gray-500">
                            Turn on if the client has asked for Peak to help set them up. It adds a one-time $199.99 onboarding fee.
                        </p>
                        <p className="text-blue-700 bg-blue-50 rounded-lg p-3 border border-blue-200">
                            <span className="font-semibold">Affects:</span> The fee is added to the first invoice only and is never repeated on renewals.
                        </p>
                        <label className="flex items-center gap-3 cursor-pointer w-fit">
                            <input
                                type="checkbox"
                                className="toggle toggle-sm"
                                checked={config.onboardingRequested}
                                onChange={(e) => setConfigField("onboardingRequested", e.target.checked)}
                            />
                            <span className="text-gray-700">{config.onboardingRequested ? "Yes, add the onboarding fee" : "No onboarding"}</span>
                        </label>
                    </div>

                    <AthleteSubscriptionSelect
                        orgType={config.orgType}
                        value={config.athleteSubscription}
                        onChange={(athleteSubscription) => setConfigField("athleteSubscription", athleteSubscription)}
                        selectClassName="select select-bordered w-full text-base disabled:opacity-50"
                    />
                </div>

                <div className="rounded-lg bg-blue-50 border border-blue-100 p-4 text-blue-900 flex flex-col gap-1.5">
                    <p className="font-semibold">What happens when you click Create</p>
                    <ul className="list-disc pl-4 flex flex-col gap-1">
                        <li>The organisation and these settings are saved, and you&apos;re taken to its detail page.</li>
                        <li>No owner is assigned yet. Add one from the Overview tab so someone can log in and manage it.</li>
                        <li>
                            No free trial starts and no invoice is sent from here. Per the billing docs, invoices are only raised for
                            organisations that signed up through PeakAMS, so billing for this organisation has to be arranged separately.
                        </li>
                    </ul>
                </div>

                {error && (
                    <p className="text-red-500">{error}</p>
                )}

                <div className="flex items-center justify-end gap-3 pt-1">
                    <Link
                        to="/commercial/organisations"
                        className="btn btn-ghost"
                    >
                        Cancel
                    </Link>
                    <button
                        type="submit"
                        disabled={saving || !name.trim()}
                        className="btn bg-gray-900 text-white hover:bg-gray-700 border-none gap-2 disabled:opacity-50"
                    >
                        {saving && <span className="loading loading-spinner loading-xs" />}
                        {saving ? "Creating…" : "Create Organisation"}
                    </button>
                </div>
            </form>
        </div>
    );
};

// ── Add user modal ────────────────────────────────────────────────────────────

type AddableRole = "owner" | "coach" | "medical" | "member";

function buildDefaultHeader(orgName: string): string {
    return `You have been added to ${orgName} on PEAK^ Elite`;
}

function buildSecondaryMessage(role: AddableRole, orgName: string, inviterName: string): string {
    return `Hi,\n\nYou have been added to ${orgName} on PEAK^ Elite as a ${role}.\n\nLog in to your existing account at ${window.location.origin}/login to get started.\n\n${inviterName}`.trim();
}

// The server appends the login email and a single-use password setup link.
function buildDefaultMessage(role: AddableRole, orgName: string, inviterName: string): string {
    return `Hi,\n\nYou have been added to ${orgName} on PEAK^ Elite as a ${role}.\n\nUse the link below to set your password, then log in at ${window.location.origin}/login.\n\n${inviterName}`.trim();
}

const ROLE_LABELS: Record<AddableRole, string> = {
    owner: "Owner",
    coach: "Coach",
    medical: "Medical Staff",
    member: "Athlete",
};

interface AddUserModalProps {
    orgId: string;
    orgName: string;
    inviterName: string;
    role: AddableRole;
    onClose: () => void;
    onAdded: (entry: OrganisationSubcollectionEntry) => void;
}

interface CheckEmailResult {
    exists: boolean;
    uid?: string;
    name?: string | null;
    surname?: string | null;
}

// Lookup only: accounts are created from the User Management page.
const AddUserModal: React.FC<AddUserModalProps> = ({ orgId, orgName, inviterName, role, onClose, onAdded }) => {
    const navigate = useNavigate();
    const [email, setEmail] = useState("");
    const [checking, setChecking] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [found, setFound] = useState<(CheckEmailResult & { checkedEmail: string }) | null>(null);
    const [notFound, setNotFound] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);

    const normalisedEmail = email.trim().toLowerCase();
    const foundMatch = found && found.checkedEmail === normalisedEmail ? found : null;
    const notFoundMatch = notFound === normalisedEmail;
    const foundName = [foundMatch?.name, foundMatch?.surname].filter(Boolean).join(" ");

    const handleSearch = async (e: React.FormEvent) => {
        e.preventDefault();
        setChecking(true);
        setError(null);
        setFound(null);
        setNotFound(null);

        try {
            const checkEmail = httpsCallable<{ email: string }, CheckEmailResult>(functions, "checkUserEmailAsAdmin");
            const { data } = await checkEmail({ email: email.trim() });
            if (data.exists && data.uid) setFound({ ...data, checkedEmail: normalisedEmail });
            else setNotFound(normalisedEmail);
        } catch (err: any) {
            console.error("[AddUserModal] Email check failed:", err);
            setError(err?.message ?? "Failed to search for user. Please try again.");
        } finally {
            setChecking(false);
        }
    };

    const handleAdd = async () => {
        if (!foundMatch) return;
        setSaving(true);
        setError(null);

        try {
            const addUser = httpsCallable<
                {
                    email: string;
                    role: AddableRole;
                    orgId: string;
                    header: string;
                    message: string;
                    secondaryMessage: string;
                },
                { success: boolean; uid: string; userAlreadyExisted: boolean; emailSent: boolean; message: string }
            >(functions, "createUserViaAdmin");

            const result = await addUser({
                email: email.trim(),
                role,
                orgId,
                header: buildDefaultHeader(orgName),
                message: buildDefaultMessage(role, orgName, inviterName),
                secondaryMessage: buildSecondaryMessage(role, orgName, inviterName),
            });
            setSuccess(result.data.message);
            onAdded({ id: result.data.uid, userId: result.data.uid, role, status: "active" });
        } catch (err: any) {
            console.error("[AddUserModal] Failed:", err);
            setError(err?.message ?? "Failed to add user. Please try again.");
        } finally {
            setSaving(false);
        }
    };

    const changeEmail = () => {
        setFound(null);
        setNotFound(null);
        setError(null);
    };

    const goToCreateUser = () => {
        navigate(`/users?createUser=1&email=${encodeURIComponent(email.trim())}`);
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/40" onClick={onClose} />
            <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-md flex flex-col overflow-hidden">
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
                    <h2 className="font-semibold text-gray-900">
                        Add {ROLE_LABELS[role]}
                    </h2>
                    <button onClick={onClose} className="btn btn-ghost btn-xs btn-square">
                        <XMarkIcon className="h-4 w-4" />
                    </button>
                </div>

                {/* Body */}
                <form onSubmit={handleSearch} className="px-6 py-5 flex flex-col gap-4">
                    {foundMatch || notFoundMatch ? (
                        <div className="flex items-center justify-between text-sm bg-gray-50 border rounded-lg px-3 py-2">
                            <span className="truncate">{foundMatch ? foundMatch.checkedEmail : email.trim()}</span>
                            {!success && (
                                <button type="button" onClick={changeEmail} className="text-xs text-primary hover:underline shrink-0 ml-3">
                                    Change
                                </button>
                            )}
                        </div>
                    ) : (
                        <div className="flex flex-col gap-1.5">
                            <label className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Search by email <span className="text-red-400">*</span>
                            </label>
                            <input
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="user@example.com"
                                className="input input-bordered w-full"
                                required
                                autoFocus
                            />
                        </div>
                    )}

                    {error && <p className="text-sm text-red-500">{error}</p>}
                    {success && <p className="text-sm text-green-600">{success}</p>}

                    {foundMatch && !success && (
                        <div className="text-sm bg-green-50 border border-green-200 text-green-800 rounded-lg p-3 flex flex-col gap-2">
                            <div>
                                User found{foundName ? `: ${foundName}` : ""}.
                                <div className="text-xs mt-1 font-mono break-all">UID: {foundMatch.uid}</div>
                            </div>
                        </div>
                    )}

                    {notFoundMatch && !success && (
                        <div className="text-sm bg-amber-50 border border-amber-200 text-amber-800 rounded-lg p-3 flex flex-col gap-2">
                            <div>No user exists with this email. Create the account from User Management first, then add them here.</div>
                        </div>
                    )}

                    {/* Footer */}
                    <div className="flex items-center justify-end gap-3 pt-1">
                        <button type="button" onClick={onClose} className="btn btn-ghost">
                            {success ? "Close" : "Cancel"}
                        </button>
                        {!success && (foundMatch ? (
                            <button
                                type="button"
                                onClick={handleAdd}
                                disabled={saving}
                                className="btn bg-gray-900 text-white hover:bg-gray-700 border-none gap-2 disabled:opacity-50"
                            >
                                {saving && <span className="loading loading-spinner loading-xs" />}
                                {saving ? "Adding…" : `Add as ${ROLE_LABELS[role]}`}
                            </button>
                        ) : notFoundMatch ? (
                            <button
                                type="button"
                                onClick={goToCreateUser}
                                className="btn bg-gray-900 text-white hover:bg-gray-700 border-none"
                            >
                                Go to User ManagementOka
                            </button>
                        ) : (
                            <button
                                type="submit"
                                disabled={checking || !email.trim()}
                                className="btn bg-gray-900 text-white hover:bg-gray-700 border-none gap-2 disabled:opacity-50"
                            >
                                {checking && <span className="loading loading-spinner loading-xs" />}
                                {checking ? "Searching…" : "Search"}
                            </button>
                        ))}
                    </div>
                </form>
            </div>
        </div>
    );
};

// ── User avatar + name cell ───────────────────────────────────────────────────

const UserCell: React.FC<{ uid: string; userData: EnrichedUser | null | undefined }> = ({ uid, userData }) => {
    const initials = userData
        ? `${userData.name?.[0] ?? ""}${userData.surname?.[0] ?? ""}`.toUpperCase() || "?"
        : "?";
    const displayName = userData
        ? [userData.name, userData.surname].filter(Boolean).join(" ") || "No Name"
        : "No Name";

    return (
        <div className="flex items-center gap-3">
            {userData?.imageUrl ? (
                <img src={userData.imageUrl} alt={displayName ?? uid} className="h-8 w-8 rounded-full object-cover flex-shrink-0" />
            ) : (
                <div className="h-8 w-8 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0 text-xs font-semibold text-gray-500">
                    {initials}
                </div>
            )}
            <div className="flex flex-col min-w-0">
                {displayName ? (
                    <>
                        <span className="text-sm font-medium text-gray-900 truncate">{displayName}</span>
                    </>
                ) : (
                    <span className="text-sm text-gray-400 font-mono truncate">{uid}</span>
                )}
                <p className="line-clamp-1 text-ellipsis text-xs text-gray-400">{userData?.email ?? "—"}</p>
            </div>
        </div>
    );
};

// ── Coach teams modal ─────────────────────────────────────────────────────────

interface CoachTeamsModalProps {
    orgId: string;
    coach: OrganisationSubcollectionEntry;
    userData: EnrichedUser | null | undefined;
    allTeams: string[];
    onClose: () => void;
    onSaved: (coachId: string, teams: string[] | null) => void;
}

const CoachTeamsModal: React.FC<CoachTeamsModalProps> = ({ orgId, coach, userData, allTeams, onClose, onSaved }) => {
    // null = all teams; string[] = specific selection
    const initialTeams: string[] | null = Array.isArray(coach.teams) ? coach.teams : null;
    const [allTeamsMode, setAllTeamsMode] = useState<boolean>(initialTeams === null);
    const [selected, setSelected] = useState<Set<string>>(new Set(initialTeams ?? []));
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const displayName = userData
        ? [userData.name, userData.surname].filter(Boolean).join(" ") || userData.username || coach.id
        : coach.id;

    const toggle = (team: string) => {
        setSelected((prev) => {
            const next = new Set(prev);
            next.has(team) ? next.delete(team) : next.add(team);
            return next;
        });
    };

    const handleSave = async () => {
        setSaving(true);
        setError(null);
        try {
            const teamsValue = allTeamsMode ? null : Array.from(selected);
            await updateCoachTeams(orgId, coach.id, teamsValue);
            onSaved(coach.id, teamsValue);
            onClose();
        } catch (err) {
            console.error("[CoachTeamsModal] Failed to save:", err);
            setError("Failed to save. Please try again.");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/40" onClick={onClose} />
            <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-md flex flex-col overflow-hidden">
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
                    <div>
                        <h2 className="font-semibold text-gray-900">Assign Teams</h2>
                        <p className="text-xs text-gray-400 mt-0.5">{displayName}</p>
                    </div>
                    <button onClick={onClose} className="btn btn-ghost btn-xs btn-square">
                        <XMarkIcon className="h-4 w-4" />
                    </button>
                </div>

                {/* Body */}
                <div className="px-6 py-5 flex flex-col gap-4">
                    {/* All teams toggle */}
                    <label className="flex items-center justify-between cursor-pointer p-3 rounded-xl bg-gray-50 border border-gray-200">
                        <div>
                            <span className="text-sm font-medium text-gray-800">All teams</span>
                            <p className="text-xs text-gray-400 mt-0.5">Coach can see athletes from every team</p>
                        </div>
                        <input
                            type="checkbox"
                            className="toggle toggle-sm"
                            checked={allTeamsMode}
                            onChange={(e) => setAllTeamsMode(e.target.checked)}
                        />
                    </label>

                    {/* Per-team checkboxes */}
                    {!allTeamsMode && (
                        allTeams.length === 0 ? (
                            <p className="text-sm text-gray-400 text-center py-2">No teams have been created yet.</p>
                        ) : (
                            <ul className="flex flex-col gap-2 max-h-56 overflow-y-auto">
                                {allTeams.map((team) => (
                                    <li key={team}>
                                        <label className="flex items-center gap-3 cursor-pointer p-3 rounded-xl hover:bg-gray-50 border border-gray-100">
                                            <input
                                                type="checkbox"
                                                className="checkbox checkbox-sm"
                                                checked={selected.has(team)}
                                                onChange={() => toggle(team)}
                                            />
                                            <span className="text-sm text-gray-800">{team}</span>
                                        </label>
                                    </li>
                                ))}
                            </ul>
                        )
                    )}

                    {error && <p className="text-sm text-red-500">{error}</p>}
                </div>

                {/* Footer */}
                <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100">
                    <button onClick={onClose} className="btn btn-ghost">Cancel</button>
                    <button
                        onClick={handleSave}
                        disabled={saving || (!allTeamsMode && selected.size === 0 && allTeams.length > 0)}
                        className="btn bg-gray-900 text-white hover:bg-gray-700 border-none gap-2 disabled:opacity-50"
                    >
                        {saving && <span className="loading loading-spinner loading-xs" />}
                        {saving ? "Saving…" : "Save"}
                    </button>
                </div>
            </div>
        </div>
    );
};

// ── Medical teams modal ───────────────────────────────────────────────────────

interface MedicalTeamsModalProps {
    orgId: string;
    medical: OrganisationSubcollectionEntry;
    userData: EnrichedUser | null | undefined;
    allTeams: string[];
    onClose: () => void;
    onSaved: (medicalId: string, teams: string[] | null) => void;
}

const MedicalTeamsModal: React.FC<MedicalTeamsModalProps> = ({ orgId, medical, userData, allTeams, onClose, onSaved }) => {
    const initialTeams: string[] | null = Array.isArray(medical.teams) ? medical.teams : null;
    const [allTeamsMode, setAllTeamsMode] = useState<boolean>(initialTeams === null);
    const [selected, setSelected] = useState<Set<string>>(new Set(initialTeams ?? []));
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const displayName = userData
        ? [userData.name, userData.surname].filter(Boolean).join(" ") || userData.username || medical.id
        : medical.id;

    const toggle = (team: string) => {
        setSelected((prev) => {
            const next = new Set(prev);
            next.has(team) ? next.delete(team) : next.add(team);
            return next;
        });
    };

    const handleSave = async () => {
        setSaving(true);
        setError(null);
        try {
            const teamsValue = allTeamsMode ? null : Array.from(selected);
            await updateMedicalTeams(orgId, medical.id, teamsValue);
            onSaved(medical.id, teamsValue);
            onClose();
        } catch (err) {
            console.error("[MedicalTeamsModal] Failed to save:", err);
            setError("Failed to save. Please try again.");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/40" onClick={onClose} />
            <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-md flex flex-col overflow-hidden">
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
                    <div>
                        <h2 className="font-semibold text-gray-900">Assign Teams</h2>
                        <p className="text-xs text-gray-400 mt-0.5">{displayName}</p>
                    </div>
                    <button onClick={onClose} className="btn btn-ghost btn-xs btn-square">
                        <XMarkIcon className="h-4 w-4" />
                    </button>
                </div>
                <div className="px-6 py-5 flex flex-col gap-4">
                    <label className="flex items-center justify-between cursor-pointer p-3 rounded-xl bg-gray-50 border border-gray-200">
                        <div>
                            <span className="text-sm font-medium text-gray-800">All teams</span>
                            <p className="text-xs text-gray-400 mt-0.5">Medical staff can see athletes from every team</p>
                        </div>
                        <input
                            type="checkbox"
                            className="toggle toggle-sm"
                            checked={allTeamsMode}
                            onChange={(e) => setAllTeamsMode(e.target.checked)}
                        />
                    </label>
                    {!allTeamsMode && (
                        allTeams.length === 0 ? (
                            <p className="text-sm text-gray-400 text-center py-2">No teams have been created yet.</p>
                        ) : (
                            <ul className="flex flex-col gap-2 max-h-56 overflow-y-auto">
                                {allTeams.map((team) => (
                                    <li key={team}>
                                        <label className="flex items-center gap-3 cursor-pointer p-3 rounded-xl hover:bg-gray-50 border border-gray-100">
                                            <input
                                                type="checkbox"
                                                className="checkbox checkbox-sm"
                                                checked={selected.has(team)}
                                                onChange={() => toggle(team)}
                                            />
                                            <span className="text-sm text-gray-800">{team}</span>
                                        </label>
                                    </li>
                                ))}
                            </ul>
                        )
                    )}
                    {error && <p className="text-sm text-red-500">{error}</p>}
                </div>
                <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100">
                    <button onClick={onClose} className="btn btn-ghost">Cancel</button>
                    <button
                        onClick={handleSave}
                        disabled={saving || (!allTeamsMode && selected.size === 0 && allTeams.length > 0)}
                        className="btn bg-gray-900 text-white hover:bg-gray-700 border-none gap-2 disabled:opacity-50"
                    >
                        {saving && <span className="loading loading-spinner loading-xs" />}
                        {saving ? "Saving…" : "Save"}
                    </button>
                </div>
            </div>
        </div>
    );
};

// ── Athlete teams modal ───────────────────────────────────────────────────────

interface AthleteTeamsModalProps {
    orgId: string;
    member: Pick<OrganisationMember, "userId" | "joinedAt"> & { team?: string | null; groups?: string[] };
    userData: EnrichedUser | null | undefined;
    allTeams: string[];
    allGroups: Record<string, string[]>;
    onClose: () => void;
    onSaved: (userId: string, team: string | null, groups: string[]) => void;
}

const AthleteTeamsModal: React.FC<AthleteTeamsModalProps> = ({ orgId, member, userData, allTeams, allGroups, onClose, onSaved }) => {
    const initialTeam: string | null = typeof member.team === "string" ? member.team : null;
    const initialGroup: string | null = Array.isArray(member.groups) && member.groups.length > 0 ? member.groups[0] : null;
    const [selectedTeam, setSelectedTeam] = useState<string | null>(initialTeam);
    const [selectedGroup, setSelectedGroup] = useState<string | null>(initialGroup);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const displayName = userData
        ? [userData.name, userData.surname].filter(Boolean).join(" ") || userData.username || member.userId
        : member.userId;

    const teamGroups = selectedTeam ? (allGroups[selectedTeam] ?? []) : [];

    const handleSelectTeam = (team: string | null) => {
        setSelectedTeam(team);
        setSelectedGroup(null);
    };

    const handleSave = async () => {
        setSaving(true);
        setError(null);
        try {
            const groupsValue = selectedTeam && selectedGroup ? [selectedGroup] : [];
            await updateMemberTeam(orgId, member.userId, selectedTeam);
            await updateMemberGroups(orgId, member.userId, groupsValue);
            onSaved(member.userId, selectedTeam, groupsValue);
            onClose();
        } catch (err) {
            console.error("[AthleteTeamsModal] Failed to save:", err);
            setError("Failed to save. Please try again.");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/40" onClick={onClose} />
            <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-md flex flex-col overflow-hidden">
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
                    <div>
                        <h2 className="font-semibold text-gray-900">Assign Team &amp; Group</h2>
                        <p className="text-xs text-gray-400 mt-0.5">{displayName}</p>
                    </div>
                    <button onClick={onClose} className="btn btn-ghost btn-xs btn-square">
                        <XMarkIcon className="h-4 w-4" />
                    </button>
                </div>

                <div className="px-6 py-5 flex flex-col gap-2">
                    {allTeams.length === 0 ? (
                        <p className="text-sm text-gray-400 text-center py-2">No teams have been created yet.</p>
                    ) : (
                        <ul className="flex flex-col gap-2 max-h-72 overflow-y-auto">
                            <li>
                                <label className="flex items-center gap-3 cursor-pointer p-3 rounded-xl hover:bg-gray-50 border border-gray-100">
                                    <input
                                        type="radio"
                                        className="radio radio-sm"
                                        checked={selectedTeam === null}
                                        onChange={() => handleSelectTeam(null)}
                                    />
                                    <span className="text-sm text-gray-400 italic">No team</span>
                                </label>
                            </li>
                            {allTeams.map((team) => (
                                <li key={team}>
                                    <label className="flex items-center gap-3 cursor-pointer p-3 rounded-xl hover:bg-gray-50 border border-gray-100">
                                        <input
                                            type="radio"
                                            className="radio radio-sm"
                                            checked={selectedTeam === team}
                                            onChange={() => handleSelectTeam(team)}
                                        />
                                        <span className="text-sm text-gray-800">{team}</span>
                                    </label>
                                </li>
                            ))}
                        </ul>
                    )}

                    {selectedTeam && teamGroups.length > 0 && (
                        <>
                            <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mt-3">Group</p>
                            <ul className="flex flex-col gap-2 max-h-52 overflow-y-auto">
                                <li>
                                    <label className="flex items-center gap-3 cursor-pointer p-3 rounded-xl hover:bg-gray-50 border border-gray-100">
                                        <input
                                            type="radio"
                                            className="radio radio-sm"
                                            checked={selectedGroup === null}
                                            onChange={() => setSelectedGroup(null)}
                                        />
                                        <span className="text-sm text-gray-400 italic">No group</span>
                                    </label>
                                </li>
                                {teamGroups.map((group) => (
                                    <li key={group}>
                                        <label className="flex items-center gap-3 cursor-pointer p-3 rounded-xl hover:bg-gray-50 border border-gray-100">
                                            <input
                                                type="radio"
                                                className="radio radio-sm"
                                                checked={selectedGroup === group}
                                                onChange={() => setSelectedGroup(group)}
                                            />
                                            <span className="text-sm text-gray-800">{group}</span>
                                        </label>
                                    </li>
                                ))}
                            </ul>
                        </>
                    )}

                    {error && <p className="text-sm text-red-500">{error}</p>}
                </div>

                <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100">
                    <button onClick={onClose} className="btn btn-ghost">Cancel</button>
                    <button
                        onClick={handleSave}
                        disabled={saving}
                        className="btn bg-gray-900 text-white hover:bg-gray-700 border-none gap-2 disabled:opacity-50"
                    >
                        {saving && <span className="loading loading-spinner loading-xs" />}
                        {saving ? "Saving…" : "Save"}
                    </button>
                </div>
            </div>
        </div>
    );
};

// ── Teams modal ───────────────────────────────────────────────────────────────

interface TeamsModalProps {
    orgId: string;
    initial: string[];
    onClose: () => void;
    onSaved: (teams: string[]) => void;
}

const TeamsModal: React.FC<TeamsModalProps> = ({ orgId, initial, onClose, onSaved }) => {
    const [teams, setTeams] = useState<string[]>(initial);
    const [input, setInput] = useState("");
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const addTeam = () => {
        const val = input.trim();
        if (!val || teams.includes(val)) return;
        setTeams((prev) => [...prev, val]);
        setInput("");
    };

    const removeTeam = (i: number) => setTeams((prev) => prev.filter((_, idx) => idx !== i));

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === "Enter") { e.preventDefault(); addTeam(); }
    };

    const handleSave = async () => {
        setSaving(true);
        setError(null);
        try {
            await updateOrganisationTeams(orgId, teams);
            onSaved(teams);
            onClose();
        } catch (err) {
            console.error("[TeamsModal] Failed to save:", err);
            setError("Failed to save. Please try again.");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            {/* Backdrop */}
            <div className="absolute inset-0 bg-black/40" onClick={onClose} />

            {/* Panel */}
            <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-xl flex flex-col gap-0 overflow-hidden">
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
                    <h2 className="font-semibold text-gray-900">Manage Teams</h2>
                    <button onClick={onClose} className="btn btn-ghost btn-xs btn-square">
                        <XMarkIcon className="h-4 w-4" />
                    </button>
                </div>

                {/* Body */}
                <div className="px-6 py-5 flex flex-col gap-4">
                    {/* Add input */}
                    <div className="flex gap-2">
                        <input
                            type="text"
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            onKeyDown={handleKeyDown}
                            placeholder="Team name…"
                            className="input input-bordered flex-1"
                        />
                        <button
                            type="button"
                            onClick={addTeam}
                            disabled={!input.trim()}
                            className="btn bg-gray-900 text-white hover:bg-gray-700 border-none gap-1.5 disabled:opacity-40"
                        >
                            <PlusIcon className="h-4 w-4" />
                            Add
                        </button>
                    </div>

                    {/* Team list */}
                    {teams.length === 0 ? (
                        <p className="text-sm text-gray-400 text-center py-4">No teams added yet.</p>
                    ) : (
                        <ul className="space-y-2 max-h-64 overflow-y-auto">
                            {teams.map((team, i) => (
                                <li
                                    key={i}
                                    className="flex items-center justify-between bg-gray-100 rounded-xl px-4 py-3"
                                >
                                    <span className="text-sm font-medium text-gray-800">{team}</span>
                                    <button
                                        type="button"
                                        onClick={() => removeTeam(i)}
                                        className="text-gray-400 hover:text-red-500 transition-colors ml-4"
                                        aria-label={`Remove ${team}`}
                                    >
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <path d="M18 6L6 18M6 6l12 12" />
                                        </svg>
                                    </button>
                                </li>
                            ))}
                        </ul>
                    )}

                    {error && <p className="text-sm text-red-500">{error}</p>}
                </div>

                {/* Footer */}
                <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100">
                    <button onClick={onClose} className="btn btn-ghost">Cancel</button>
                    <button
                        onClick={handleSave}
                        disabled={saving}
                        className="btn bg-gray-900 text-white hover:bg-gray-700 border-none gap-2 disabled:opacity-50"
                    >
                        {saving && <span className="loading loading-spinner loading-xs" />}
                        {saving ? "Saving…" : "Save"}
                    </button>
                </div>
            </div>
        </div>
    );
};

// ── Detail view ───────────────────────────────────────────────────────────────

const OrganisationDetailPage: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [organisation, setOrganisation] = useState<Organisation | null>(null);
    const [owners, setOwners] = useState<OrganisationSubcollectionEntry[]>([]);
    const [coaches, setCoaches] = useState<OrganisationSubcollectionEntry[]>([]);
    const [medicals, setMedicals] = useState<OrganisationSubcollectionEntry[]>([]);
    const [userDataMap, setUserDataMap] = useState<Record<string, EnrichedUser | null>>({});
    const [loading, setLoading] = useState(true);
    const [teamsModalOpen, setTeamsModalOpen] = useState(false);
    const [addUserRole, setAddUserRole] = useState<AddableRole | null>(null);
    const [coachModal, setCoachModal] = useState<OrganisationSubcollectionEntry | null>(null);
    const [medicalModal, setMedicalModal] = useState<OrganisationSubcollectionEntry | null>(null);
    const [athleteModal, setAthleteModal] = useState<(Pick<OrganisationMember, "userId" | "joinedAt"> & { team?: string | null; groups?: string[] }) | null>(null);
    const [memberSubDocs, setMemberSubDocs] = useState<Record<string, { team: string | null; groups: string[] }>>({});
    const [addingGroupToTeam, setAddingGroupToTeam] = useState<string | null>(null);
    const [newGroupName, setNewGroupName] = useState("");
    const [editingName, setEditingName] = useState(false);
    const [nameInput, setNameInput] = useState("");
    const [savingName, setSavingName] = useState(false);
    const [recheckOpen, setRecheckOpen] = useState(false);
    const [recheckDetail, setRecheckDetail] = useState("Rechecking users in this organisation only.");
    const [recheckingSubscriptions, setRecheckingSubscriptions] = useState(false);
    const [recheckEntries, setRecheckEntries] = useState<RecheckEntry[]>([]);
    const [processedRechecks, setProcessedRechecks] = useState(0);
    const [memberNotesMap, setMemberNotesMap] = useState<Record<string, OrganisationMemberNote[]>>({});
    const [notesLoading, setNotesLoading] = useState(false);
    const [orgInvitations, setOrgInvitations] = useState<Invitation[]>([]);
    const [loadingOrgInvitations, setLoadingOrgInvitations] = useState(false);
    const [sendingOrgInvitation, setSendingOrgInvitation] = useState(false);
    const [newInvitationEmail, setNewInvitationEmail] = useState("");
    const [processingOrgInvitations, setProcessingOrgInvitations] = useState<Set<string>>(new Set());
    const [invitationError, setInvitationError] = useState<string | null>(null);
    const [invoices, setInvoices] = useState<StripeInvoiceSummary[] | null>(null);
    const [loadingInvoices, setLoadingInvoices] = useState(false);
    const [invoicesError, setInvoicesError] = useState<string | null>(null);

    const [restoringOwner, setRestoringOwner] = useState(false);
    const [restoreOwnerError, setRestoreOwnerError] = useState<string | null>(null);

    // View toggle
    const [view, setView] = useState<"overview" | "settings" | "billing">("overview");

    // Config / settings
    const [orgConfig, setOrgConfig] = useState<OrganisationConfig>(DEFAULT_ORGANISATION_CONFIG);
    const [lastSavedOrgConfig, setLastSavedOrgConfig] = useState<OrganisationConfig>(DEFAULT_ORGANISATION_CONFIG);
    const [loadingConfig, setLoadingConfig] = useState(false);
    const [savingConfig, setSavingConfig] = useState(false);
    const [configError, setConfigError] = useState<string | null>(null);
    const [configSaved, setConfigSaved] = useState(false);

    const isNew = id === "new";

    useEffect(() => {
        if (isNew) {
            setLoading(false);
            return;
        }
        if (!id) return;
        const load = async () => {
            setLoading(true);
            try {
                const [orgData, ownersData, coachesData, medicalsData, membersSubData] = await Promise.all([
                    fetchOrganisationById(id),
                    fetchOrganisationSubcollection(id, "owners"),
                    fetchOrganisationSubcollection(id, "coaches"),
                    fetchOrganisationSubcollection(id, "medicals"),
                    fetchOrganisationSubcollection(id, "members"),
                ]);
                const match = orgData;
                setOrganisation(match);
                setOwners(ownersData);
                setCoaches(coachesData);
                setMedicals(medicalsData);
                setMemberSubDocs(Object.fromEntries(membersSubData.map((d) => [
                    d.id,
                    {
                        team: typeof d.team === "string" ? d.team : null,
                        groups: Array.isArray(d.groups) ? d.groups.filter((v): v is string => typeof v === "string") : [],
                    },
                ])));
                setMemberNotesMap({});

                // Collect all unique UIDs from owners, coaches, medicals, and members
                const allUids = new Set<string>([
                    ...ownersData.map((e) => e.id),
                    ...coachesData.map((e) => e.id),
                    ...medicalsData.map((e) => e.id),
                    ...(match?.members ?? []).map((m) => m.userId),
                    ...(match?.ownerId ? [match.ownerId] : []),
                ]);

                const entries = await Promise.all(
                    Array.from(allUids).map(async (uid) => {
                        const data = await adminService.searchUserByUid(uid);
                        return [uid, data] as [string, EnrichedUser | null];
                    })
                );
                setUserDataMap(Object.fromEntries(entries));

                if (match?.members?.length) {
                    setNotesLoading(true);
                    try {
                        const noteEntries = await Promise.all(
                            match.members.map(async (member) => {
                                const notes = await fetchOrganisationMemberNotes(id, member.userId);
                                return [member.userId, notes] as [string, OrganisationMemberNote[]];
                            })
                        );
                        setMemberNotesMap(Object.fromEntries(noteEntries));
                    } finally {
                        setNotesLoading(false);
                    }
                } else {
                    setNotesLoading(false);
                }
            } catch (err) {
                console.error("[OrganisationDetailPage] Failed to load:", err);
            } finally {
                setLoading(false);
            }
        };
        load();
    }, [id, isNew]);

    const legacyOwnerId = organisation?.ownerId;
    const legacyOwnerEntry = legacyOwnerId ? owners.find((entry) => entry.id === legacyOwnerId) : undefined;
    const ownerDocIssue: OwnerDocIssue | null = !legacyOwnerId
        ? null
        : !legacyOwnerEntry
            ? "missing"
            : legacyOwnerEntry.status === undefined || legacyOwnerEntry.status === "active"
                ? null
                : "inactive";
    const legacyOwnerData = legacyOwnerId ? userDataMap[legacyOwnerId] : undefined;

    const handleRestoreOwner = async () => {
        if (!id || !legacyOwnerId) return;
        const email = legacyOwnerData?.email;
        if (!email) {
            setRestoreOwnerError("This user has no email on their profile. Add them as an owner manually with their login email.");
            return;
        }
        setRestoringOwner(true);
        setRestoreOwnerError(null);
        try {
            const createUser = httpsCallable<
                { email: string; role: AddableRole; orgId: string },
                { uid: string }
            >(functions, "createUserViaAdmin");
            const { data } = await createUser({ email, role: "owner", orgId: id });
            const entry: OrganisationSubcollectionEntry = { id: data.uid, userId: data.uid, role: "owner", status: "active" };
            setOwners((prev) => [...prev.filter((owner) => owner.id !== data.uid), entry]);
            if (data.uid !== legacyOwnerId) {
                setRestoreOwnerError(`${email} belongs to a different account (${data.uid}), which was added as owner instead. Check this organisation's owners.`);
            }
        } catch (err: any) {
            console.error("[OrganisationDetailPage] Failed to restore owner:", err);
            setRestoreOwnerError(err?.message ?? "Failed to restore owner access.");
        } finally {
            setRestoringOwner(false);
        }
    };

    const getGroups = (team: string): string[] => {
        return organisation?.groups?.[team] ?? [];
    };

    const saveGroups = async (updatedGroups: Record<string, string[]>) => {
        if (!organisation) return;
        await updateOrganisationGroups(organisation.id, updatedGroups);
        setOrganisation((prev) => prev ? { ...prev, groups: updatedGroups } : prev);
    };

    const handleAddGroup = async (team: string) => {
        const name = newGroupName.trim();
        if (!name || !organisation) return;
        const current = organisation.groups ?? {};
        const teamGroups = current[team] ?? [];
        if (teamGroups.includes(name)) return;
        const updated = { ...current, [team]: [...teamGroups, name] };
        setNewGroupName("");
        setAddingGroupToTeam(null);
        await saveGroups(updated);
    };

    const handleRemoveGroup = async (team: string, group: string) => {
        if (!organisation) return;
        const current = organisation.groups ?? {};
        const updated = { ...current, [team]: (current[team] ?? []).filter((g) => g !== group) };
        await saveGroups(updated);
    };

    const handleSaveName = async () => {
        if (!organisation || !nameInput.trim() || nameInput.trim() === organisation.name) {
            setEditingName(false);
            return;
        }
        setSavingName(true);
        try {
            await updateOrganisationName(organisation.id, nameInput.trim());
            setOrganisation((prev) => prev ? { ...prev, name: nameInput.trim() } : prev);
            setEditingName(false);
        } catch (err) {
            console.error("[OrganisationDetailPage] Failed to update name:", err);
        } finally {
            setSavingName(false);
        }
    };

    const recheckSubscriptionForUser = async (userId: string) => {
        try {
            const triggerCheckUserSubscription = httpsCallable(functions, "triggerCheckUserSubscription");
            await triggerCheckUserSubscription({ userId });

            // Refresh user data so the latest subscription is reflected in the UI.
            const refreshedUserData = await adminService.searchUserByUid(userId);
            setUserDataMap((prev) => ({ ...prev, [userId]: refreshedUserData }));
        } catch (error) {
            console.error(`[OrganisationDetailPage] Failed to recheck subscription for ${userId}:`, error);
        }
    };

    const handleRecheckSubscriptions = async (roles?: Array<RecheckEntry["role"]>) => {
        if (recheckingSubscriptions || !organisation) return;

        const roleFilter = Array.isArray(roles) ? new Set(roles) : null;
        const usersToCheck = [
            ...owners.map((entry) => ({ role: "owner" as const, userId: entry.id })),
            ...coaches.map((entry) => ({ role: "coach" as const, userId: entry.id })),
            ...medicals.map((entry) => ({ role: "medical" as const, userId: entry.id })),
            ...organisation.members.map((member) => ({ role: "athlete" as const, userId: member.userId })),
        ].filter((entry) => !roleFilter || roleFilter.has(entry.role));

        // Keep only one check per uid while preserving first role label found.
        const dedupedUsers = Array.from(
            usersToCheck.reduce<Map<string, { role: "owner" | "coach" | "medical" | "athlete"; userId: string }>>(
                (acc, entry) => {
                    if (!acc.has(entry.userId)) acc.set(entry.userId, entry);
                    return acc;
                },
                new Map()
            ).values()
        );

        if (dedupedUsers.length === 0) return;

        setRecheckDetail(
            roles?.length === 1 && roles[0] === "athlete"
                ? "Updating athlete subscriptions for this organisation."
                : "Rechecking users in this organisation only."
        );
        setRecheckOpen(true);
        setRecheckingSubscriptions(true);
        setRecheckEntries([]);
        setProcessedRechecks(0);

        const triggerCheckUserSubscription = httpsCallable(functions, "triggerCheckUserSubscription");
        let entryCounter = 0;

        try {
            for (const user of dedupedUsers) {
                const entryId = `${user.role}-${user.userId}-${entryCounter++}`;

                setRecheckEntries((prev) => [
                    ...prev,
                    {
                        id: entryId,
                        role: user.role,
                        userId: user.userId,
                        status: "checking",
                    },
                ]);

                try {
                    await triggerCheckUserSubscription({ userId: user.userId });
                    setRecheckEntries((prev) =>
                        prev.map((entry) =>
                            entry.id === entryId ? { ...entry, status: "success" } : entry
                        )
                    );
                } catch (error) {
                    const message =
                        error instanceof Error
                            ? error.message
                            : "Failed to recheck subscription";

                    setRecheckEntries((prev) =>
                        prev.map((entry) =>
                            entry.id === entryId
                                ? { ...entry, status: "failed", error: message }
                                : entry
                        )
                    );
                } finally {
                    setProcessedRechecks((prev) => prev + 1);
                }
            }

            // Re-fetch users after recheck completes so updated subscriptions are reflected.
            try {
                const refreshedEntries = await Promise.all(
                    dedupedUsers.map(async ({ userId }) => {
                        const userData = await adminService.searchUserByUid(userId);
                        return [userId, userData] as const;
                    })
                );
                setUserDataMap((prev) => ({ ...prev, ...Object.fromEntries(refreshedEntries) }));
            } catch (refreshError) {
                console.error("[OrganisationDetailPage] Failed to refresh users after subscription recheck:", refreshError);
            }
        } catch (error) {
            console.error("[OrganisationDetailPage] Subscription recheck failed:", error);
        } finally {
            setRecheckingSubscriptions(false);
        }
    };

    const totalRechecks = recheckEntries.length;
    const checkingCount = recheckEntries.filter((entry) => entry.status === "checking").length;
    const successCount = recheckEntries.filter((entry) => entry.status === "success").length;
    const failedCount = recheckEntries.filter((entry) => entry.status === "failed").length;

    const allMemberNotes: OrganisationMemberNote[] = organisation
        ? organisation.members
            .flatMap((member) => memberNotesMap[member.userId] ?? [])
            .sort((a, b) => (b.updatedAt?.getTime() ?? b.createdAt?.getTime() ?? 0) - (a.updatedAt?.getTime() ?? a.createdAt?.getTime() ?? 0))
        : [];
    // Load org config alongside the org
    useEffect(() => {
        if (!id || isNew) return;
        setLoadingConfig(true);
        getOrganisationConfig(id)
            .then((config) => {
                setOrgConfig(config);
                setLastSavedOrgConfig(config);
            })
            .catch((err) => console.error("[OrganisationDetailPage] Failed to load config:", err))
            .finally(() => setLoadingConfig(false));
    }, [id, isNew]);

    const handleSaveConfig = async () => {
        if (!id) return;
        setSavingConfig(true);
        setConfigError(null);
        setConfigSaved(false);
        // Commercial athlete plans are stored explicitly. Other org types always grant Premium.
        const configToSave: OrganisationConfig = orgConfig.orgType === "commercial"
            ? { ...orgConfig, athleteSubscription: resolveAthleteSubscription(orgConfig.athleteSubscription) }
            : { ...orgConfig, athleteSubscription: "premium" };
        const orgTypeChanged = configToSave.orgType !== lastSavedOrgConfig.orgType;
        const athletePlanChanged = configToSave.athleteSubscription !== resolveAthleteSubscription(lastSavedOrgConfig.athleteSubscription);
        try {
            await saveOrganisationConfig(id, configToSave);
            setOrgConfig(configToSave);
            setLastSavedOrgConfig(configToSave);
            setConfigSaved(true);
            setTimeout(() => setConfigSaved(false), 3000);
            if (orgTypeChanged) {
                void handleRecheckSubscriptions();
            } else if (athletePlanChanged && configToSave.orgType === "commercial") {
                void handleRecheckSubscriptions(["athlete"]);
            }
        } catch (err) {
            console.error("[OrganisationDetailPage] Failed to save config:", err);
            setConfigError("Failed to save settings. Please try again.");
        } finally {
            setSavingConfig(false);
        }
    };

    // Lazily fetch invoices the first time the Billing tab is opened
    useEffect(() => {
        if (view !== "billing" || !id || isNew || invoices !== null || loadingInvoices) return;
        setLoadingInvoices(true);
        setInvoicesError(null);
        const fn = httpsCallable(functions, "getPeakEliteCoachInvoices");
        fn({ orgId: id })
            .then((result) => {
                const data = result.data as { success: boolean; invoices: StripeInvoiceSummary[] };
                setInvoices(data.invoices ?? []);
            })
            .catch((err) => {
                console.error("[OrganisationDetailPage] Failed to load invoices:", err);
                setInvoicesError("Failed to load invoices. Please try again.");
            })
            .finally(() => setLoadingInvoices(false));
    }, [view, id, isNew, invoices, loadingInvoices]);

    // Fetch invitations for this org
    useEffect(() => {
        if (!id || isNew) return;
        setLoadingOrgInvitations(true);
        invitationService.getInvitationsByOrg(id).then((inv) => {
            setOrgInvitations(inv);
        }).catch((err) => {
            console.error("[OrganisationDetailPage] Failed to load invitations:", err);
        }).finally(() => setLoadingOrgInvitations(false));
    }, [id, isNew]);

    const handleSendOrgInvitation = async (e: React.FormEvent) => {
        e.preventDefault();
        const ownerId = owners[0]?.id;
        if (!ownerId) return;

        const emails = Array.from(new Set(
            newInvitationEmail
                .split(",")
                .map((email) => email.trim().toLowerCase())
                .filter(Boolean)
        ));
        if (emails.length === 0) return;

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        const invalidEmails = emails.filter((email) => !emailRegex.test(email));
        if (invalidEmails.length > 0) {
            setInvitationError(`Invalid email${invalidEmails.length > 1 ? "s" : ""}: ${invalidEmails.join(", ")}`);
            return;
        }

        setSendingOrgInvitation(true);
        setInvitationError(null);
        try {
            const inviteAthleteViaAdmin = httpsCallable(functions, "inviteAthleteViaAdmin");
            const results = await Promise.allSettled(
                emails.map((email) => inviteAthleteViaAdmin({ coachId: ownerId, athleteEmail: email, orgId: id }))
            );
            const failed = emails.filter((_, i) => results[i].status === "rejected");
            if (failed.length === emails.length) {
                const firstError = results.find((r): r is PromiseRejectedResult => r.status === "rejected");
                setInvitationError(firstError?.reason?.message ?? "Failed to send invitations. Please try again.");
            } else {
                if (failed.length > 0) {
                    setInvitationError(`Failed to send to: ${failed.join(", ")}`);
                }
                setNewInvitationEmail("");
            }
            const updated = await invitationService.getInvitationsByOrg(id!);
            setOrgInvitations(updated);
        } catch (err: any) {
            console.error("[OrganisationDetailPage] Failed to send invitation:", err);
            setInvitationError(err?.message ?? "Failed to send invitation. Please try again.");
        } finally {
            setSendingOrgInvitation(false);
        }
    };

    const handleAcceptOrgInvitation = async (invitation: Invitation) => {
        if (!invitation.id) return;
        setProcessingOrgInvitations((prev) => new Set(prev).add(invitation.id!));
        setInvitationError(null);
        try {
            const manuallyAddAthlete = httpsCallable(functions, "manuallyAddAthlete");
            await manuallyAddAthlete({ invitationId: invitation.id });
            const updated = await invitationService.getInvitationsByOrg(id!);
            setOrgInvitations(updated);
        } catch (err: any) {
            console.error("[OrganisationDetailPage] Failed to accept invitation:", err);
            setInvitationError(err?.message ?? "Failed to accept invitation. Please try again.");
        } finally {
            setProcessingOrgInvitations((prev) => {
                const next = new Set(prev);
                next.delete(invitation.id!);
                return next;
            });
        }
    };

    const formatInvitationDate = (date: Date | undefined) => {
        if (!date) return "—";
        return new Intl.DateTimeFormat("en-AU", { day: "numeric", month: "short", year: "numeric" }).format(date);
    };

    return (
        <DashboardWrapper>
            <div className="p-6 flex flex-col items-center gap-6 min-h-[calc(100vh-64px)]">
                <div className="w-full">
                    <Link
                        to="/commercial/organisations"
                        className="btn btn-sm btn-ghost gap-2"
                    >
                        <ArrowLeftIcon className="h-4 w-4" />
                        Organisations
                    </Link>
                </div>

                {loading ? (
                    <div className="flex-1 flex items-center justify-center py-24">
                        <span className="loading loading-spinner loading-md text-gray-400" />
                    </div>
                ) : isNew ? (
                    <CreateOrganisationForm />
                ) : !organisation ? (
                    <div className="flex-1 flex flex-col items-center justify-center gap-2 text-gray-400 py-24 w-full max-w-2xl">
                        <BuildingOffice2Icon className="h-10 w-10" />
                        <p className="text-sm">Organisation not found</p>
                    </div>
                ) : (
                    <div className="flex flex-col gap-6 w-full max-w-4xl">
                        {/* Header card */}
                        <div className="bg-white rounded-xl border border-gray-200 p-6 flex items-start gap-4 relative">
                            {organisation.imageUrl ? (
                                <img
                                    src={organisation.imageUrl}
                                    alt={organisation.name}
                                    className="h-16 w-16 rounded-xl object-cover flex-shrink-0"
                                />
                            ) : (
                                <div className="h-16 w-16 rounded-xl bg-gray-100 flex items-center justify-center flex-shrink-0">
                                    <BuildingOffice2Icon className="h-8 w-8 text-gray-400" />
                                </div>
                            )}
                            <div className="flex-1 min-w-0">
                                {editingName ? (
                                    <div className="flex items-center gap-2">
                                        <input
                                            autoFocus
                                            type="text"
                                            value={nameInput}
                                            onChange={(e) => setNameInput(e.target.value)}
                                            onKeyDown={(e) => {
                                                if (e.key === "Enter") handleSaveName();
                                                if (e.key === "Escape") setEditingName(false);
                                            }}
                                            className="input input-bordered input-sm flex-1 text-base font-bold"
                                        />
                                        <button
                                            onClick={handleSaveName}
                                            disabled={savingName || !nameInput.trim()}
                                            className="btn btn-sm bg-gray-900 text-white hover:bg-gray-700 border-none disabled:opacity-50"
                                        >
                                            {savingName ? <span className="loading loading-spinner loading-xs" /> : "Save"}
                                        </button>
                                        <button
                                            onClick={() => setEditingName(false)}
                                            className="btn btn-sm btn-ghost"
                                        >
                                            Cancel
                                        </button>
                                    </div>
                                ) : (
                                    <div className="flex items-center gap-2 group">
                                        <h1 className="text-xl font-bold text-gray-900">{organisation.name}</h1>
                                        <button
                                            onClick={() => { setNameInput(organisation.name); setEditingName(true); }}
                                            className="opacity-0 group-hover:opacity-100 transition-opacity btn btn-ghost btn-xs btn-square"
                                        >
                                            <PencilSquareIcon className="h-3.5 w-3.5 text-gray-400" />
                                        </button>
                                    </div>
                                )}
                                {organisation.description && (
                                    <p className="text-sm text-gray-500 mt-1">{organisation.description}</p>
                                )}
                                <div className="flex items-center gap-3 text-sm text-gray-400 mt-2">
                                    <span className="flex items-center gap-1.5">
                                        <UserGroupIcon className="h-4 w-4" />
                                        {organisation.members.length} member{organisation.members.length !== 1 ? "s" : ""}
                                    </span>
                                    <div className="badge badge-outline text-xs capitalize">{orgConfig.orgType}</div>
                                    {orgConfig.orgType === "commercial" && (
                                        <div className="badge badge-outline text-xs">
                                            Athletes: {ATHLETE_SUBSCRIPTION_LABELS[resolveAthleteSubscription(orgConfig.athleteSubscription)]}
                                        </div>
                                    )}
                                    {orgConfig.billingPeriodEnd && (
                                        <span className="text-xs text-gray-400">
                                            Billing period ends {formatMemberDate(orgConfig.billingPeriodEnd)}
                                        </span>
                                    )}
                                </div>
                            </div>
                            <div className="flex flex-col items-end gap-2 flex-shrink-0">
                                <button
                                    onClick={() => { void handleRecheckSubscriptions(); }}
                                    disabled={recheckingSubscriptions}
                                    className="btn btn-sm btn-ghost gap-2 text-gray-500 hover:text-gray-900"
                                    title="Recheck subscriptions for users in this organisation"
                                >
                                    <ArrowPathIcon className={`h-4 w-4 ${recheckingSubscriptions ? "animate-spin" : ""}`} />
                                    {recheckingSubscriptions ? "Rechecking…" : "Recheck Subscriptions"}
                                </button>
                            </div>
                        </div>

                        {/* Tab bar */}
                        <div className="flex items-center gap-1 bg-gray-100 p-1 rounded-xl self-start">
                            <button
                                onClick={() => setView("overview")}
                                className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                                    view === "overview"
                                        ? "bg-white text-gray-900 shadow-sm"
                                        : "text-gray-500 hover:text-gray-700"
                                }`}
                            >
                                Overview
                            </button>
                            <button
                                onClick={() => setView("settings")}
                                className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-colors flex items-center gap-1.5 ${
                                    view === "settings"
                                        ? "bg-white text-gray-900 shadow-sm"
                                        : "text-gray-500 hover:text-gray-700"
                                }`}
                            >
                                <Cog6ToothIcon className="h-3.5 w-3.5" />
                                Settings
                            </button>
                            <button
                                onClick={() => setView("billing")}
                                className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-colors flex items-center gap-1.5 ${
                                    view === "billing"
                                        ? "bg-white text-gray-900 shadow-sm"
                                        : "text-gray-500 hover:text-gray-700"
                                }`}
                            >
                                <BanknotesIcon className="h-3.5 w-3.5" />
                                Billing
                            </button>
                        </div>

                        {/* Billing view */}
                        {view === "billing" && (
                            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                                <div className="px-5 py-4 border-b border-gray-100">
                                    <h2 className="font-semibold text-gray-900">Invoices</h2>
                                    <p className="text-xs text-gray-400 mt-0.5">Stripe invoices billed to this organisation.</p>
                                </div>
                                {loadingInvoices ? (
                                    <div className="flex justify-center py-12">
                                        <span className="loading loading-spinner loading-sm text-gray-400" />
                                    </div>
                                ) : invoicesError ? (
                                    <div className="flex flex-col items-center justify-center gap-2 text-gray-400 py-12">
                                        <p className="text-sm text-red-500">{invoicesError}</p>
                                    </div>
                                ) : !invoices || invoices.length === 0 ? (
                                    <div className="flex flex-col items-center justify-center gap-2 text-gray-400 py-12">
                                        <BanknotesIcon className="h-8 w-8" />
                                        <p className="text-sm">No invoices</p>
                                    </div>
                                ) : (
                                    <div className="w-full text-sm">
                                        <div className="grid grid-cols-6 bg-gray-50 text-xs text-gray-500 uppercase tracking-wider">
                                            <div className="font-medium py-3 px-4">Invoice</div>
                                            <div className="font-medium py-3 px-4">Date</div>
                                            <div className="font-medium py-3 px-4">Status</div>
                                            <div className="font-medium py-3 px-4">Amount Due</div>
                                            <div className="font-medium py-3 px-4">Amount Paid</div>
                                            <div className="font-medium py-3 px-4 text-right">Download</div>
                                        </div>
                                        <div className="divide-y divide-gray-100">
                                            {invoices.map((invoice) => (
                                                <div key={invoice.id} className="grid grid-cols-6 items-center hover:bg-gray-50 transition-colors">
                                                    <div className="py-3 px-4 text-gray-900 font-medium">
                                                        {invoice.number ?? invoice.id}
                                                    </div>
                                                    <div className="py-3 px-4 text-gray-500">{formatInvoiceDate(invoice.created)}</div>
                                                    <div className="py-3 px-4">
                                                        <span className={`badge badge-sm capitalize ${invoiceStatusBadgeClass(invoice.status)}`}>
                                                            {invoice.status ?? "unknown"}
                                                        </span>
                                                    </div>
                                                    <div className="py-3 px-4 text-gray-500">{formatInvoiceAmount(invoice.amount_due, invoice.currency)}</div>
                                                    <div className="py-3 px-4 text-gray-500">{formatInvoiceAmount(invoice.amount_paid, invoice.currency)}</div>
                                                    <div className="py-3 px-4 text-right">
                                                        {(invoice.invoice_pdf || invoice.hosted_invoice_url) ? (
                                                            <a
                                                                href={invoice.invoice_pdf ?? invoice.hosted_invoice_url ?? undefined}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="btn btn-ghost btn-xs gap-1.5 text-gray-500 hover:text-gray-900"
                                                            >
                                                                <ArrowDownTrayIcon className="h-3.5 w-3.5" />
                                                                Download
                                                            </a>
                                                        ) : (
                                                            <span className="text-xs text-gray-300">—</span>
                                                        )}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Settings view */}
                        {view === "settings" && (
                            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                                <div className="px-5 py-4 border-b border-gray-100 flex items-start justify-between gap-3">
                                    <div>
                                        <h2 className="font-semibold text-gray-900">Organisation Settings</h2>
                                        <p className="text-gray-500 mt-0.5">Admin-configured settings for this organisation.</p>
                                    </div>
                                    <button
                                        onClick={handleSaveConfig}
                                        disabled={savingConfig || loadingConfig}
                                        className="btn btn-sm bg-gray-900 text-white hover:bg-gray-700 border-none gap-2 disabled:opacity-50"
                                    >
                                        {savingConfig && <span className="loading loading-spinner loading-xs" />}
                                        {savingConfig ? "Saving…" : "Save Settings"}
                                    </button>
                                </div>
                                <div className="px-5 py-5 flex flex-col gap-6">
                                    {loadingConfig ? (
                                        <div className="flex justify-center py-6">
                                            <span className="loading loading-spinner loading-sm text-gray-400" />
                                        </div>
                                    ) : (
                                        <>
                                            <div className="flex flex-col gap-6 pb-6 border-b border-gray-100">
                                                <div>
                                                    <h3 className="font-semibold text-gray-900">Organisation type</h3>
                                                    <p className="text-gray-500 mt-0.5">Sets sensible defaults for the limits below. It decides the coach and team limits, which athlete range is allowed, and whether the client can manage their own billing.</p>
                                                </div>
                                            {/* Org type */}
                                            <div className="flex flex-col gap-1.5">
                                                <label className="font-medium text-gray-800">Organisation Type</label>
                                                <p className="text-gray-500">Individual is one coach, Team is a club with several coaches, and Commercial is for bespoke or enterprise deals. It decides the coach and team limits and whether the client can manage their own billing.</p>
                                                <AffectsCallout>Changing it presets Max Coaches and Max Teams (Individual: 1 and 1, Team: 10 and 10) and locks them. Only Commercial lets you edit them, and only Commercial can choose the athlete subscription.</AffectsCallout>
                                                <select
                                                    value={orgConfig.orgType}
                                                    onChange={(e) => {
                                                        const nextType = e.target.value as OrganisationConfig["orgType"];
                                                        setOrgConfig((prev) => ({
                                                            ...prev,
                                                            orgType: nextType,
                                                            ...(nextType === "individual" && { maxCoaches: 1, maxTeams: 1 }),
                                                            ...(nextType === "team" && { maxCoaches: 10, maxTeams: 10 }),
                                                            ...(nextType !== "commercial" && { athleteSubscription: "premium" as const }),
                                                        }));
                                                    }}
                                                    className="select select-bordered w-full text-base"
                                                >
                                                    <option value="individual">Individual</option>
                                                    <option value="team">Team</option>
                                                    <option value="commercial">Commercial</option>
                                                </select>
                                            </div>
                                            </div>

                                            <div className="flex flex-col gap-6 pb-6 border-b border-gray-100">
                                                <div>
                                                    <h3 className="font-semibold text-gray-900">Seats and limits</h3>
                                                    <p className="text-gray-500 mt-0.5">How much of the product the organisation is allowed to use. These limits drive pricing.</p>
                                                </div>
                                            {/* Max athletes */}
                                            <div className="flex flex-col gap-1.5">
                                                <label className="font-medium text-gray-800">Max Athletes</label>
                                                <p className="text-gray-500">How many athletes can be active in the organisation. These are the athlete monitoring seats. Set to 0 for unlimited.</p>
                                                <AffectsCallout>The price of the plan and how many athletes the organisation can add. The client cannot lower this below their current active athletes.</AffectsCallout>
                                                <input
                                                    type="number"
                                                    min={0}
                                                    value={orgConfig.maxAthletes}
                                                    onChange={(e) => setOrgConfig((prev) => ({ ...prev, maxAthletes: Math.max(0, parseInt(e.target.value) || 0) }))}
                                                    className="input input-bordered w-full text-base"
                                                />
                                            </div>

                                            {/* Max athletes for PAS */}
                                            <div className="flex flex-col gap-1.5">
                                                <label className="font-medium text-gray-800">Max Athletes (PAS)</label>
                                                <p className="text-gray-500">How many athletes can have a PAS (Programs and Sessions) seat, which lets coaches build programs and sessions for them. Use 0 if the client isn&apos;t buying it.</p>
                                                <AffectsCallout>The Programming line on the invoice. It should never be higher than Max Athletes.</AffectsCallout>
                                                <input
                                                    type="number"
                                                    min={0}
                                                    value={orgConfig.maxAthletesForPas}
                                                    onChange={(e) => setOrgConfig((prev) => ({ ...prev, maxAthletesForPas: Math.max(0, parseInt(e.target.value) || 0) }))}
                                                    className="input input-bordered w-full text-base"
                                                />
                                            </div>

                                            <div className="flex flex-col gap-1.5">
                                                <label className="font-medium text-gray-800">Max Coaches</label>
                                                <p className="text-gray-500">
                                                    {orgConfig.orgType === "commercial"
                                                        ? "How many coaches the organisation can have. Set this to match the agreed deal."
                                                        : "Preset by the organisation type, so it is locked. Switch to Commercial to set it by hand."}
                                                </p>
                                                <AffectsCallout>How many coaches can be added to the organisation.</AffectsCallout>
                                                <input
                                                    type="number"
                                                    min={0}
                                                    value={orgConfig.maxCoaches}
                                                    disabled={orgConfig.orgType !== "commercial"}
                                                    onChange={(e) => setOrgConfig((prev) => ({ ...prev, maxCoaches: Math.max(0, parseInt(e.target.value) || 0) }))}
                                                    className="input input-bordered w-full text-base disabled:opacity-50"
                                                />
                                            </div>

                                            <div className="flex flex-col gap-1.5">
                                                <label className="font-medium text-gray-800">Max Teams</label>
                                                <p className="text-gray-500">
                                                    {orgConfig.orgType === "commercial"
                                                        ? "How many teams the organisation can create. Set this to match the agreed deal."
                                                        : "Preset by the organisation type, so it is locked. Switch to Commercial to set it by hand."}
                                                </p>
                                                <AffectsCallout>How many teams can be created to group athletes and coaches.</AffectsCallout>
                                                <input
                                                    type="number"
                                                    min={0}
                                                    value={orgConfig.maxTeams}
                                                    disabled={orgConfig.orgType !== "commercial"}
                                                    onChange={(e) => setOrgConfig((prev) => ({ ...prev, maxTeams: Math.max(0, parseInt(e.target.value) || 0) }))}
                                                    className="input input-bordered w-full text-base disabled:opacity-50"
                                                />
                                            </div>
                                            </div>

                                            <div className="flex flex-col gap-6 ">
                                                <div>
                                                    <h3 className="font-semibold text-gray-900">Billing and subscriptions</h3>
                                                    <p className="text-gray-500 mt-0.5">How the organisation is invoiced and what its athletes receive.</p>
                                                </div>
                                            {/* Billing period */}
                                            <div className="flex flex-col gap-1.5">
                                                <label className="font-medium text-gray-800">Billing Period</label>
                                                <p className="text-gray-500">How often the organisation is invoiced. Annual is the discounted option (about 30% cheaper overall). Quarterly costs more in exchange for flexibility.</p>
                                                <AffectsCallout>The amount on each invoice and how long each billing period runs. Changing it recalculates the billing period end date.</AffectsCallout>
                                                <select
                                                    value={orgConfig.billingPeriod ?? ""}
                                                    onChange={(e) => {
                                                        const billingPeriod = (e.target.value || undefined) as OrganisationConfig["billingPeriod"];
                                                        setOrgConfig((prev) => ({
                                                            ...prev,
                                                            billingPeriod,
                                                            billingPeriodEnd: prev.billingPeriodStart
                                                                ? calculateBillingPeriodEnd(prev.billingPeriodStart, billingPeriod)
                                                                : prev.billingPeriodEnd,
                                                        }));
                                                    }}
                                                    className="select select-bordered w-full text-base"
                                                >
                                                    <option value="" disabled>Not set</option>
                                                    <option value="quarterly">Quarterly</option>
                                                    <option value="annually">Annually</option>
                                                </select>
                                            </div>

                                            {/* Billing period start / end */}
                                            <div className="flex flex-col gap-1.5">
                                                <label className="font-medium text-gray-800">Billing Period Start</label>
                                                <p className="text-gray-500">Start of the current billing period. The end date is calculated automatically.</p>
                                                <AffectsCallout>The billing period end date, which is recalculated from this start date and the billing period.</AffectsCallout>
                                                <input
                                                    type="date"
                                                    value={toDateInputValue(orgConfig.billingPeriodStart)}
                                                    onChange={(e) => {
                                                        const start = e.target.value ? new Date(e.target.value) : undefined;
                                                        setOrgConfig((prev) => ({
                                                            ...prev,
                                                            billingPeriodStart: start,
                                                            billingPeriodEnd: start ? calculateBillingPeriodEnd(start, prev.billingPeriod) : undefined,
                                                        }));
                                                    }}
                                                    className="input input-bordered w-full text-base"
                                                />
                                            </div>

                                            <div className="flex flex-col gap-1.5">
                                                <label className="font-medium text-gray-800">Billing Period End</label>
                                                <p className="text-gray-500">Calculated from the start date and billing period, so it can&apos;t be edited directly.</p>
                                                <AffectsCallout>Nothing on its own. Change the billing period start or the billing period to move it.</AffectsCallout>
                                                <input
                                                    type="date"
                                                    value={toDateInputValue(orgConfig.billingPeriodEnd)}
                                                    disabled
                                                    className="input input-bordered w-full text-base disabled:opacity-50"
                                                />
                                            </div>

                                            <div className="flex flex-col gap-1.5">
                                                <label className="flex items-center justify-between cursor-pointer p-3 rounded-xl bg-gray-50 border border-gray-200 hover:bg-gray-100 transition-colors">
                                                    <div>
                                                        <span className="font-medium text-gray-800">Onboarding Requested</span>
                                                        <p className="text-gray-500 mt-0.5">Turn on if the client has asked for Peak to help set them up. It adds a one-time $199.99 onboarding fee.</p>
                                                    </div>
                                                    <input
                                                        type="checkbox"
                                                        className="toggle toggle-sm"
                                                        checked={orgConfig.onboardingRequested}
                                                        onChange={(e) => setOrgConfig((prev) => ({ ...prev, onboardingRequested: e.target.checked }))}
                                                    />
                                                </label>
                                                <AffectsCallout>The fee is added to the first invoice only and is never repeated on renewals.</AffectsCallout>
                                            </div>

                                            <div className="flex flex-col gap-1.5">
                                                <AthleteSubscriptionSelect
                                                    orgType={orgConfig.orgType}
                                                    value={orgConfig.athleteSubscription}
                                                    onChange={(athleteSubscription) => setOrgConfig((prev) => ({ ...prev, athleteSubscription }))}
                                                    selectClassName="select select-bordered w-full text-base disabled:opacity-50"
                                                    labelClassName="font-medium text-gray-800"
                                                    helpClassName="text-gray-500"
                                                />
                                                <AffectsCallout>The plan every athlete in the organisation receives. Saving updates all current athletes.</AffectsCallout>
                                            </div>
                                            </div>

                                            {configError && <p className="text-sm text-red-500">{configError}</p>}

                                            <div className="flex items-center gap-3 pt-1">
                                                <button
                                                    onClick={handleSaveConfig}
                                                    disabled={savingConfig}
                                                    className="btn btn-sm bg-gray-900 text-white hover:bg-gray-700 border-none gap-2 disabled:opacity-50"
                                                >
                                                    {savingConfig && <span className="loading loading-spinner loading-xs" />}
                                                    {savingConfig ? "Saving…" : "Save Settings"}
                                                </button>
                                                {configSaved && <p className="text-sm text-green-600">Settings saved</p>}
                                            </div>
                                        </>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* Overview */}
                        {view === "overview" && <>

                        {(ownerDocIssue || restoreOwnerError) && legacyOwnerId && (
                            <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 flex flex-col gap-3">
                                <div className="flex items-start gap-3">
                                    <ExclamationTriangleIcon className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                                    <div className="text-sm text-amber-900 flex flex-col gap-1">
                                        {ownerDocIssue && (
                                            <>
                                                <p className="font-medium">
                                                    {ownerDocIssue === "missing"
                                                        ? "The organisation's owner has no owners doc"
                                                        : "The organisation's owner has an inactive owners doc"}
                                                </p>
                                                <p>
                                                    Owner access now comes only from an active owners doc, not the
                                                    organisation's <code>ownerId</code>. Without one, this user loses owner access.
                                                    If they were deliberately removed as owner, leave it as is.
                                                </p>
                                            </>
                                        )}
                                        {restoreOwnerError && <p className="text-red-700">{restoreOwnerError}</p>}
                                    </div>
                                </div>
                                {ownerDocIssue && (
                                    <div className="flex items-center justify-between gap-3 flex-wrap bg-white rounded-lg border border-amber-200 px-3 py-2">
                                        <div className="flex items-center gap-3 min-w-0">
                                            <UserCell uid={legacyOwnerId} userData={legacyOwnerData} />
                                            <span className="text-xs text-gray-400 font-mono hidden lg:inline">{legacyOwnerId}</span>
                                        </div>
                                        <button
                                            onClick={handleRestoreOwner}
                                            disabled={restoringOwner || legacyOwnerData === undefined}
                                            className="btn btn-sm bg-gray-900 text-white hover:bg-gray-700 border-none gap-2 disabled:opacity-50"
                                        >
                                            {restoringOwner && <span className="loading loading-spinner loading-xs" />}
                                            Restore owner access
                                        </button>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Owners */}
                        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                            <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                    <h2 className="font-semibold text-gray-900">Owners</h2>
                                    <span className="px-2 py-1 rounded-lg bg-gray-100 text-gray-400 text-sm">{owners.length}</span>
                                </div>
                                <button onClick={() => setAddUserRole("owner")} className="btn btn-ghost btn-sm gap-1.5 text-gray-500 hover:text-gray-900">
                                    <PlusIcon className="h-3.5 w-3.5" />
                                    Add
                                </button>
                            </div>
                            {owners.length === 0 ? (
                                <div className="flex flex-col items-center justify-center gap-2 text-gray-400 py-12">
                                    <UserGroupIcon className="h-8 w-8" />
                                    <p className="text-sm">No owners</p>
                                </div>
                            ) : (
                                <div className="w-full text-sm">
                                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 bg-gray-50 text-xs text-gray-500 uppercase tracking-wider">
                                        <div className="font-medium py-3 px-4 col-span-2">User</div>
                                        <div className="font-medium py-3 px-4 hidden lg:block">UID</div>
                                        <div className="font-medium py-3 px-4 hidden xl:block">Subscription</div>
                                        <div className="font-medium py-3 px-4 hidden sm:block">Joined</div>
                                    </div>
                                    <div className="divide-y divide-gray-100">
                                        {owners.map((entry) => {
                                            const ud = userDataMap[entry.id];
                                            const activeSubscription = formatSubscriptionLabel(
                                                typeof ud?.subscription?.subscriptionType === "string"
                                                    ? ud.subscription.subscriptionType
                                                    : "default",
                                                typeof ud?.subscription?.subscriptionVia === "string"
                                                    ? ud.subscription.subscriptionVia
                                                    : "default"
                                            );
                                            return (
                                                <div
                                                    key={entry.id}
                                                    onClick={() => navigate(`/users?user=${entry.id}`)}
                                                    className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 items-center hover:bg-gray-50 transition-colors cursor-pointer"
                                                >
                                                    <div className="py-3 px-4 lg:col-span-2">
                                                        <UserCell uid={entry.id} userData={ud} />
                                                    </div>
                                                    <div className="py-3 px-4 hidden lg:block text-xs text-gray-400 font-mono">
                                                        <p className="line-clamp-1 text-ellipsis">
                                                            {entry.id}
                                                        </p>
                                                    </div>
                                                    <div className="py-3 px-4 hidden xl:block text-sm text-gray-500">
                                                        <div className="badge badge-outline text-xs capitalize line-clamp-1">
                                                            {activeSubscription}
                                                        </div>
                                                    </div>
                                                    <div className="py-3 px-4 hidden sm:block text-sm text-gray-500">
                                                        {entry.joinedAt ? formatMemberDate(entry.joinedAt) : "—"}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Coaches */}
                        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                            <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                    <h2 className="font-semibold text-gray-900">Coaches</h2>
                                    <span className="px-2 py-1 rounded-lg bg-gray-100 text-gray-400 text-sm">{coaches.length}</span>
                                </div>
                                <button onClick={() => setAddUserRole("coach")} className="btn btn-ghost btn-sm gap-1.5 text-gray-500 hover:text-gray-900">
                                    <PlusIcon className="h-3.5 w-3.5" />
                                    Add
                                </button>
                            </div>
                            {coaches.length === 0 ? (
                                <div className="flex flex-col items-center justify-center gap-2 text-gray-400 py-12">
                                    <UserGroupIcon className="h-8 w-8" />
                                    <p className="text-sm">No coaches</p>
                                </div>
                            ) : (
                                <div className="w-full text-sm">
                                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 bg-gray-50 text-xs text-gray-500 uppercase tracking-wider">
                                        <div className="font-medium py-3 px-4 md:col-span-2">User</div>
                                        <div className="font-medium py-3 px-4">Teams</div>
                                        <div className="font-medium py-3 px-4 hidden xl:block">Subscription</div>
                                        <div className="font-medium py-3 px-4 hidden sm:block">Joined</div>
                                    </div>
                                    <div className="divide-y divide-gray-100">
                                        {coaches.map((entry) => {
                                            const ud = userDataMap[entry.id];
                                            const assignedTeams: string[] | null = Array.isArray(entry.teams) ? entry.teams : null;
                                            const activeSubscription = formatSubscriptionLabel(
                                                typeof ud?.subscription?.subscriptionType === "string"
                                                    ? ud.subscription.subscriptionType
                                                    : "default",
                                                typeof ud?.subscription?.subscriptionVia === "string"
                                                    ? ud.subscription.subscriptionVia
                                                    : "default"
                                            );
                                            return (
                                                <div
                                                    key={entry.id}
                                                    className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 items-center hover:bg-gray-50 transition-colors cursor-pointer"
                                                    onClick={() => setCoachModal(entry)}
                                                >
                                                    <div
                                                        className="py-3 px-4 md:col-span-2"
                                                        onClick={(e) => { e.stopPropagation(); navigate(`/users?user=${entry.id}`); }}
                                                    >
                                                        <UserCell uid={entry.id} userData={ud} />
                                                    </div>
                                                    <div className="py-3 px-4">
                                                        {assignedTeams === null ? (
                                                            <span className="px-2 py-0.5 rounded-md bg-gray-100 text-gray-500 text-xs">All teams</span>
                                                        ) : assignedTeams.length === 0 ? (
                                                            <span className="px-2 py-0.5 rounded-md bg-amber-50 text-amber-600 text-xs">None</span>
                                                        ) : (
                                                            <div className="flex flex-wrap gap-1">
                                                                {assignedTeams.map((t) => (
                                                                    <span key={t} className="px-2 py-0.5 rounded-md bg-gray-100 text-gray-700 text-xs">{t}</span>
                                                                ))}
                                                            </div>
                                                        )}
                                                    </div>
                                                    <div className="py-3 px-4 hidden xl:block text-sm text-gray-500">
                                                        <div className="badge badge-outline text-xs capitalize line-clamp-1">
                                                            {activeSubscription}
                                                        </div>
                                                    </div>
                                                    <div className="py-3 px-4 hidden sm:block text-sm text-gray-500">
                                                        {entry.joinedAt ? formatMemberDate(entry.joinedAt) : "—"}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}
                        </div>

                        {coachModal && (
                            <CoachTeamsModal
                                orgId={organisation.id}
                                coach={coachModal}
                                userData={userDataMap[coachModal.id]}
                                allTeams={organisation.teams ?? []}
                                onClose={() => setCoachModal(null)}
                                onSaved={(coachId, teams) => {
                                    setCoaches((prev) =>
                                        prev.map((c) => c.id === coachId ? { ...c, teams } : c)
                                    );
                                }}
                            />
                        )}

                        {/* Medical Staff */}
                        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                            <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                    <h2 className="font-semibold text-gray-900">Medical Staff</h2>
                                    <span className="px-2 py-1 rounded-lg bg-gray-100 text-gray-400 text-sm">{medicals.length}</span>
                                </div>
                                <button onClick={() => setAddUserRole("medical")} className="btn btn-ghost btn-sm gap-1.5 text-gray-500 hover:text-gray-900">
                                    <PlusIcon className="h-3.5 w-3.5" />
                                    Add
                                </button>
                            </div>
                            {medicals.length === 0 ? (
                                <div className="flex flex-col items-center justify-center gap-2 text-gray-400 py-12">
                                    <UserGroupIcon className="h-8 w-8" />
                                    <p className="text-sm">No medical staff</p>
                                </div>
                            ) : (
                                <div className="w-full text-sm">
                                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 bg-gray-50 text-xs text-gray-500 uppercase tracking-wider">
                                        <div className="font-medium py-3 px-4 md:col-span-2">User</div>
                                        <div className="font-medium py-3 px-4">Teams</div>
                                        <div className="font-medium py-3 px-4 hidden xl:block">Subscription</div>
                                        <div className="font-medium py-3 px-4 hidden sm:block">Joined</div>
                                    </div>
                                    <div className="divide-y divide-gray-100">
                                        {medicals.map((entry) => {
                                            const ud = userDataMap[entry.id];
                                            const assignedTeams: string[] | null = Array.isArray(entry.teams) ? entry.teams : null;
                                            const activeSubscription = formatSubscriptionLabel(
                                                typeof ud?.subscription?.subscriptionType === "string"
                                                    ? ud.subscription.subscriptionType
                                                    : "default",
                                                typeof ud?.subscription?.subscriptionVia === "string"
                                                    ? ud.subscription.subscriptionVia
                                                    : "default"
                                            );
                                            return (
                                                <div
                                                    key={entry.id}
                                                    className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 items-center hover:bg-gray-50 transition-colors cursor-pointer"
                                                    onClick={() => setMedicalModal(entry)}
                                                >
                                                    <div
                                                        className="py-3 px-4 md:col-span-2"
                                                        onClick={(e) => { e.stopPropagation(); navigate(`/users?user=${entry.id}`); }}
                                                    >
                                                        <UserCell uid={entry.id} userData={ud} />
                                                    </div>
                                                    <div className="py-3 px-4">
                                                        {assignedTeams === null ? (
                                                            <span className="px-2 py-0.5 rounded-md bg-gray-100 text-gray-500 text-xs">All teams</span>
                                                        ) : assignedTeams.length === 0 ? (
                                                            <span className="px-2 py-0.5 rounded-md bg-amber-50 text-amber-600 text-xs">None</span>
                                                        ) : (
                                                            <div className="flex flex-wrap gap-1">
                                                                {assignedTeams.map((t) => (
                                                                    <span key={t} className="px-2 py-0.5 rounded-md bg-gray-100 text-gray-700 text-xs">{t}</span>
                                                                ))}
                                                            </div>
                                                        )}
                                                    </div>
                                                    <div className="py-3 px-4 hidden xl:block text-sm text-gray-500">
                                                        <div className="badge badge-outline text-xs capitalize line-clamp-1">
                                                            {activeSubscription}
                                                        </div>
                                                    </div>
                                                    <div className="py-3 px-4 hidden sm:block text-sm text-gray-500">
                                                        {entry.joinedAt ? formatMemberDate(entry.joinedAt) : "—"}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}
                        </div>

                        {medicalModal && (
                            <MedicalTeamsModal
                                orgId={organisation.id}
                                medical={medicalModal}
                                userData={userDataMap[medicalModal.id]}
                                allTeams={organisation.teams ?? []}
                                onClose={() => setMedicalModal(null)}
                                onSaved={(medicalId, teams) => {
                                    setMedicals((prev) =>
                                        prev.map((m) => m.id === medicalId ? { ...m, teams } : m)
                                    );
                                }}
                            />
                        )}

                        {/* Teams */}
                        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                            <div className="px-5 py-3 border-b border-gray-100 flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                    <h2 className="font-semibold text-gray-900">Teams</h2>
                                    <span className="px-2 py-1 rounded-lg bg-gray-100 text-gray-400 text-sm">{(organisation.teams ?? []).length}</span>
                                </div>
                                <button
                                    onClick={() => setTeamsModalOpen(true)}
                                    className="btn btn-ghost btn-sm gap-1.5 text-gray-500 hover:text-gray-900"
                                >
                                    <PencilSquareIcon className="h-3.5 w-3.5" />
                                    Manage
                                </button>
                            </div>
                            {!(organisation.teams ?? []).length ? (
                                <div className="flex flex-col items-center justify-center gap-2 text-gray-400 py-12">
                                    <UserGroupIcon className="h-8 w-8" />
                                    <p className="text-sm">No teams</p>
                                </div>
                            ) : (
                                <div className="divide-y divide-gray-100">
                                    {(organisation.teams as string[]).map((team) => {
                                        const groups = getGroups(team);
                                        const isAdding = addingGroupToTeam === team;
                                        return (
                                            <div key={team} className="px-5 py-4 flex flex-col gap-3">
                                                {/* Team name row */}
                                                <div className="flex items-center justify-between">
                                                    <span className="text-sm font-semibold text-gray-800">{team}</span>
                                                    <button
                                                        onClick={() => {
                                                            setAddingGroupToTeam(isAdding ? null : team);
                                                            setNewGroupName("");
                                                        }}
                                                        className="btn btn-ghost btn-xs gap-1 text-gray-400 hover:text-gray-800"
                                                    >
                                                        <PlusIcon className="h-3 w-3" />
                                                        Add group
                                                    </button>
                                                </div>

                                                {/* Groups */}
                                                {(groups.length > 0 || isAdding) && (
                                                    <div className="flex flex-wrap gap-2 pl-1">
                                                        {groups.map((group) => (
                                                            <span
                                                                key={group}
                                                                className="flex items-center gap-1.5 px-2.5 py-1 bg-gray-100 rounded-lg text-sm text-gray-700"
                                                            >
                                                                {group}
                                                                <button
                                                                    onClick={() => handleRemoveGroup(team, group)}
                                                                    className="text-gray-400 hover:text-red-500 transition-colors"
                                                                    aria-label={`Remove ${group}`}
                                                                >
                                                                    <XMarkIcon className="h-3 w-3" />
                                                                </button>
                                                            </span>
                                                        ))}

                                                        {isAdding && (
                                                            <div className="flex items-center gap-1.5">
                                                                <input
                                                                    autoFocus
                                                                    type="text"
                                                                    value={newGroupName}
                                                                    onChange={(e) => setNewGroupName(e.target.value)}
                                                                    onKeyDown={(e) => {
                                                                        if (e.key === "Enter") handleAddGroup(team);
                                                                        if (e.key === "Escape") { setAddingGroupToTeam(null); setNewGroupName(""); }
                                                                    }}
                                                                    placeholder="Group name…"
                                                                    className="input input-bordered input-xs w-36"
                                                                />
                                                                <button
                                                                    onClick={() => handleAddGroup(team)}
                                                                    disabled={!newGroupName.trim()}
                                                                    className="btn btn-xs bg-gray-900 text-white hover:bg-gray-700 border-none disabled:opacity-40"
                                                                >
                                                                    Add
                                                                </button>
                                                                <button
                                                                    onClick={() => { setAddingGroupToTeam(null); setNewGroupName(""); }}
                                                                    className="btn btn-xs btn-ghost"
                                                                >
                                                                    <XMarkIcon className="h-3 w-3" />
                                                                </button>
                                                            </div>
                                                        )}
                                                    </div>
                                                )}

                                                {groups.length === 0 && !isAdding && (
                                                    <p className="text-xs text-gray-400 pl-1">No groups</p>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>

                        {teamsModalOpen && (
                            <TeamsModal
                                orgId={organisation.id}
                                initial={organisation.teams ?? []}
                                onClose={() => setTeamsModalOpen(false)}
                                onSaved={(teams) => setOrganisation((prev) => prev ? { ...prev, teams } : prev)}
                            />
                        )}

                        {athleteModal && (
                            <AthleteTeamsModal
                                orgId={organisation.id}
                                member={athleteModal}
                                userData={userDataMap[athleteModal.userId]}
                                allTeams={organisation.teams ?? []}
                                allGroups={organisation.groups ?? {}}
                                onClose={() => setAthleteModal(null)}
                                onSaved={(userId, team, groups) => {
                                    setMemberSubDocs((prev) => ({ ...prev, [userId]: { team, groups } }));
                                }}
                            />
                        )}

                        {addUserRole && (
                            <AddUserModal
                                orgId={organisation.id}
                                orgName={organisation.name}
                                inviterName={auth.currentUser?.displayName ?? auth.currentUser?.email ?? "PEAK Admin"}
                                role={addUserRole}
                                onClose={() => setAddUserRole(null)}
                                onAdded={(entry) => {
                                    // Fetch and cache user data for the new user
                                    adminService.searchUserByUid(entry.id).then((userData) => {
                                        setUserDataMap((prev) => ({ ...prev, [entry.id]: userData }));
                                    });
                                    if (addUserRole === "owner") setOwners((prev) => [...prev, entry]);
                                    else if (addUserRole === "coach") setCoaches((prev) => [...prev, entry]);
                                    else if (addUserRole === "medical") setMedicals((prev) => [...prev, entry]);
                                    else if (addUserRole === "member") setOrganisation((prev) =>
                                        prev
                                            ? {
                                                ...prev,
                                                members: [
                                                    ...prev.members,
                                                    {
                                                        userId: entry.id,
                                                        orgId: prev.id,
                                                        role: "member",
                                                        status: "active",
                                                        joinedAt: entry.joinedAt instanceof Date ? entry.joinedAt : undefined,
                                                        team: typeof entry.team === "string" ? entry.team : null,
                                                    },
                                                ],
                                            }
                                            : prev
                                    );

                                    if (addUserRole === "owner" || addUserRole === "coach" || addUserRole === "member") {
                                        void recheckSubscriptionForUser(entry.id);
                                    }
                                }}
                            />
                        )}

                        {/* Members (Athletes) */}
                        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                            <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                    <h2 className="font-semibold text-gray-900">Athletes</h2>
                                    <span className="text-xs text-gray-400">{organisation.members.length}</span>
                                </div>
                                <button
                                    onClick={() => setAddUserRole("member")}
                                    className="btn btn-ghost btn-sm gap-1.5 text-gray-500 hover:text-gray-900"
                                >
                                    <PlusIcon className="h-3.5 w-3.5" />
                                    Add
                                </button>
                            </div>
                            {organisation.members.length === 0 ? (
                                <div className="flex flex-col items-center justify-center gap-2 text-gray-400 py-12">
                                    <UserGroupIcon className="h-8 w-8" />
                                    <p className="text-sm">No members</p>
                                </div>
                            ) : (
                                <div className="w-full text-sm">
                                    <div className="grid grid-cols-3 sm:grid-cols-4 xl:grid-cols-6 bg-gray-50 text-xs text-gray-500 uppercase tracking-wider">
                                        <div className="font-medium py-3 px-4 xl:col-span-2">User</div>
                                        <div className="font-medium py-3 px-4">Team</div>
                                        <div className="font-medium py-3 px-4">Group</div>
                                        <div className="font-medium py-3 px-4 hidden xl:block">Subscription</div>
                                        <div className="font-medium py-3 px-4 hidden sm:block">Joined</div>
                                    </div>
                                    <div className="divide-y divide-gray-100">
                                        {organisation.members.map((member) => {
                                            const ud = userDataMap[member.userId];
                                            const memberTeam = memberSubDocs[member.userId]?.team ?? null;
                                            const memberGroups = memberSubDocs[member.userId]?.groups ?? [];
                                            const activeSubscription = formatSubscriptionLabel(
                                                typeof ud?.subscription?.subscriptionType === "string"
                                                    ? ud.subscription.subscriptionType
                                                    : "default",
                                                typeof ud?.subscription?.subscriptionVia === "string"
                                                    ? ud.subscription.subscriptionVia
                                                    : "default"
                                            );
                                            return (
                                                <div
                                                    key={member.userId}
                                                    onClick={() => setAthleteModal({ userId: member.userId, team: memberTeam, groups: memberGroups, joinedAt: member.joinedAt })}
                                                    className="grid grid-cols-3 sm:grid-cols-4 xl:grid-cols-6 items-center hover:bg-gray-50 transition-colors cursor-pointer"
                                                >
                                                    <div
                                                        className="py-3 px-4 xl:col-span-2"
                                                        onClick={(e) => { e.stopPropagation(); navigate(`/users?user=${member.userId}`); }}
                                                    >
                                                        <div className="flex flex-col gap-0.5">
                                                            <UserCell uid={member.userId} userData={ud} />
                                                        </div>
                                                    </div>
                                                    <div className="py-3 px-4">
                                                        {memberTeam && (
                                                            <span className="px-2 py-0.5 rounded-md bg-gray-100 text-gray-700 text-xs line-clamp-1 text-ellipsis w-fit">{memberTeam}</span>
                                                        )}
                                                    </div>
                                                    <div className="py-3 px-4">
                                                        {memberGroups.length > 0 && (
                                                            <span className="px-2 py-0.5 rounded-md bg-gray-100 text-gray-700 text-xs">{memberGroups.join(", ")}</span>
                                                        )}
                                                    </div>
                                                    <div className="py-3 px-4 hidden xl:block text-sm text-gray-500">
                                                        <div className="badge badge-outline text-xs capitalize line-clamp-1">
                                                            {activeSubscription}
                                                        </div>
                                                    </div>
                                                    <div className="py-3 px-4 hidden sm:block text-sm text-gray-500">
                                                        {member.joinedAt ? formatMemberDate(member.joinedAt) : "—"}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Athlete Notes */}
                        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                            <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                    <h2 className="font-semibold text-gray-900">Athlete Notes</h2>
                                    <span className="px-2 py-1 rounded-lg bg-gray-100 text-gray-400 text-sm">{allMemberNotes.length}</span>
                                </div>
                            </div>
                            {notesLoading ? (
                                <div className="flex items-center justify-center py-10">
                                    <span className="loading loading-spinner loading-sm text-gray-400" />
                                </div>
                            ) : allMemberNotes.length === 0 ? (
                                <div className="flex flex-col items-center justify-center gap-2 text-gray-400 py-12">
                                    <p className="text-sm">No notes found</p>
                                </div>
                            ) : (
                                <div className="overflow-auto">
                                    <table className="table table-sm w-full">
                                        <thead className="bg-gray-50 text-xs text-gray-500 uppercase tracking-wider">
                                            <tr>
                                                <th className="font-medium py-3 px-4">Athlete</th>
                                                <th className="font-medium py-3 px-4">Note</th>
                                                <th className="font-medium py-3 px-4 hidden md:table-cell">Created By</th>
                                                <th className="font-medium py-3 px-4 hidden md:table-cell">Updated</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-100">
                                            {allMemberNotes.map((note) => {
                                                const user = userDataMap[note.memberId];
                                                return (
                                                    <tr key={`${note.memberId}-${note.id}`} className="hover:bg-gray-50 transition-colors">
                                                        <td className="py-3 px-4">
                                                            <div onClick={() => navigate(`/users?user=${note.memberId}`)} className="cursor-pointer">
                                                                <UserCell uid={note.memberId} userData={user} />
                                                            </div>
                                                        </td>
                                                        <td className="py-3 px-4 text-sm text-gray-700 max-w-xl">
                                                            <p className="line-clamp-2">{note.content || "—"}</p>
                                                        </td>
                                                        <td className="py-3 px-4 hidden md:table-cell text-xs text-gray-500 font-mono">
                                                            {note.createdBy ?? "—"}
                                                        </td>
                                                        <td className="py-3 px-4 hidden md:table-cell text-sm text-gray-500">
                                                            {note.updatedAt ? formatMemberDate(note.updatedAt) : note.createdAt ? formatMemberDate(note.createdAt) : "—"}
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>

                        {/* Invitations */}
                        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                            <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                    <h2 className="font-semibold text-gray-900">Invitations</h2>
                                    <span className="px-2 py-1 rounded-lg bg-gray-100 text-gray-400 text-sm">{orgInvitations.length}</span>
                                </div>
                            </div>

                            <div className="flex flex-col gap-4">
                                {/* Send form */}
                                <form onSubmit={handleSendOrgInvitation} className="flex gap-2 mx-4 mt-4">
                                    <input
                                        type="text"
                                        value={newInvitationEmail}
                                        onChange={(e) => setNewInvitationEmail(e.target.value)}
                                        placeholder="Athlete emails, separated by commas…"
                                        className="input input-bordered input-sm flex-1"
                                        disabled={sendingOrgInvitation || owners.length === 0}
                                    />
                                    <button
                                        type="submit"
                                        disabled={sendingOrgInvitation || !newInvitationEmail.trim() || owners.length === 0}
                                        className="btn btn-sm bg-gray-900 text-white hover:bg-gray-700 border-none gap-1.5 disabled:opacity-50"
                                    >
                                        {sendingOrgInvitation
                                            ? <span className="loading loading-spinner loading-xs" />
                                            : <PaperAirplaneIcon className="h-3.5 w-3.5" />
                                        }
                                        Send
                                    </button>
                                </form>

                                {owners.length === 0 && (
                                    <p className="text-xs text-gray-400">Add an owner to this organisation before sending invitations.</p>
                                )}

                                {invitationError && (
                                    <p className="text-sm text-red-500">{invitationError}</p>
                                )}

                                {/* List */}
                                {loadingOrgInvitations ? (
                                    <div className="flex justify-center py-6">
                                        <span className="loading loading-spinner loading-sm text-gray-400" />
                                    </div>
                                ) : orgInvitations.length === 0 ? (
                                    <div className="flex flex-col items-center justify-center gap-2 text-gray-400 py-8">
                                        <PaperAirplaneIcon className="h-7 w-7" />
                                        <p className="text-sm">No invitations sent</p>
                                    </div>
                                ) : (
                                    <table className="table table-sm w-full">
                                        <thead className="bg-gray-50 text-xs text-gray-500 uppercase tracking-wider">
                                            <tr>
                                                <th className="font-medium py-3 px-4">Athlete Email</th>
                                                <th className="font-medium py-3 px-4 hidden md:table-cell">Coach</th>
                                                <th className="font-medium py-3 px-4">Status</th>
                                                <th className="font-medium py-3 px-4 hidden sm:table-cell">Sent</th>
                                                <th className="py-3 px-4" />
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-100">
                                            {orgInvitations.map((inv) => {
                                                const coachUd = userDataMap[inv.coachId];

                                                const isProcessing = processingOrgInvitations.has(inv.id ?? "");
                                                return (
                                                    <tr key={inv.id} className="hover:bg-gray-50 transition-colors">
                                                        <td className="py-3 px-4 text-sm text-gray-900">{inv.athleteEmail}</td>
                                                        <td className="py-3 px-4 hidden md:table-cell text-sm text-gray-500">{coachUd?.name }</td>
                                                        <td className="py-3 px-4">
                                                            <span className={`px-2 py-0.5 rounded-md text-xs font-medium ${
                                                                inv.status === "accepted"
                                                                    ? "bg-green-100 text-green-700"
                                                                    : inv.status === "pending"
                                                                    ? "bg-amber-50 text-amber-600"
                                                                    : inv.status === "expired"
                                                                    ? "bg-orange-100 text-orange-600"
                                                                    : "bg-gray-100 text-gray-500"
                                                            }`}>
                                                                {inv.status}
                                                            </span>
                                                        </td>
                                                        <td className="py-3 px-4 hidden sm:table-cell text-sm text-gray-400">
                                                            {formatInvitationDate(inv.createdAt)}
                                                        </td>
                                                        <td className="py-3 px-4 text-right">
                                                            {inv.status === "pending" && (
                                                                <button
                                                                    onClick={() => handleAcceptOrgInvitation(inv)}
                                                                    disabled={isProcessing}
                                                                    className="btn btn-xs bg-gray-900 text-white hover:bg-gray-700 border-none gap-1 disabled:opacity-50"
                                                                    title="Manually accept invitation"
                                                                >
                                                                    {isProcessing
                                                                        ? <span className="loading loading-spinner loading-xs" />
                                                                        : <CheckCircleIcon className="h-3.5 w-3.5" />
                                                                    }
                                                                    Accept
                                                                </button>
                                                            )}
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                )}
                            </div>
                        </div>

                        </> /* end overview */}
                    </div>
                )}
            </div>

            {recheckOpen && (
                <div className="modal modal-open">
                    <div className="modal-box max-w-4xl">
                        <div className="flex items-start justify-between gap-4 mb-4">
                            <div>
                                <h3 className="text-lg font-semibold">Recheck Subscriptions</h3>
                                <p className="text-sm text-gray-500 mt-1">
                                    {recheckDetail}
                                </p>
                            </div>
                            <button
                                className="btn btn-sm btn-circle btn-ghost"
                                onClick={() => {
                                    if (!recheckingSubscriptions) {
                                        setRecheckOpen(false);
                                    }
                                }}
                                disabled={recheckingSubscriptions}
                            >
                                <XMarkIcon className="h-4 w-4" />
                            </button>
                        </div>

                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                            <div className="rounded-lg border p-3 bg-gray-50">
                                <p className="text-xs uppercase text-gray-500">Processed</p>
                                <p className="text-sm font-semibold">
                                    {processedRechecks}/{totalRechecks}
                                </p>
                            </div>
                            <div className="rounded-lg border p-3 bg-gray-50">
                                <p className="text-xs uppercase text-gray-500">Total checks</p>
                                <p className="text-sm font-semibold">{totalRechecks}</p>
                            </div>
                            <div className="rounded-lg border p-3 bg-gray-50">
                                <p className="text-xs uppercase text-gray-500">Successful</p>
                                <p className="text-sm font-semibold text-green-700">{successCount}</p>
                            </div>
                            <div className="rounded-lg border p-3 bg-gray-50">
                                <p className="text-xs uppercase text-gray-500">Failed</p>
                                <p className="text-sm font-semibold text-red-700">
                                    {failedCount}
                                    {checkingCount > 0 ? ` (${checkingCount} checking)` : ""}
                                </p>
                            </div>
                        </div>

                        <div className="border rounded-lg max-h-96 overflow-auto">
                            <table className="table table-sm w-full">
                                <thead className="bg-gray-50 text-xs text-gray-500 uppercase">
                                    <tr>
                                        <th>Role</th>
                                        <th>User</th>
                                        <th>Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {recheckEntries.length === 0 ? (
                                        <tr>
                                            <td colSpan={3} className="text-center text-gray-500 py-6">
                                                Starting recheck...
                                            </td>
                                        </tr>
                                    ) : (
                                        recheckEntries.map((entry) => (
                                            <tr key={entry.id}>
                                                <td className="text-sm capitalize">{entry.role}</td>
                                                <td className="text-xs font-mono">{entry.userId}</td>
                                                <td>
                                                    {entry.status === "checking" && (
                                                        <span className="badge badge-ghost badge-sm">Checking</span>
                                                    )}
                                                    {entry.status === "success" && (
                                                        <span className="badge badge-success badge-sm text-white">Success</span>
                                                    )}
                                                    {entry.status === "failed" && (
                                                        <div className="flex flex-col gap-1">
                                                            <span className="badge badge-error badge-sm text-white">Failed</span>
                                                            {entry.error && (
                                                                <span className="text-[11px] text-red-600">{entry.error}</span>
                                                            )}
                                                        </div>
                                                    )}
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>

                        <div className="modal-action">
                            <button
                                className="btn btn-ghost"
                                onClick={() => setRecheckOpen(false)}
                                disabled={recheckingSubscriptions}
                            >
                                Close
                            </button>
                        </div>
                    </div>
                    <div
                        className="modal-backdrop"
                        onClick={() => {
                            if (!recheckingSubscriptions) {
                                setRecheckOpen(false);
                            }
                        }}
                    />
                </div>
            )}
        </DashboardWrapper>
    );
};

export default OrganisationDetailPage;
